import React, { useState, useCallback, useRef, useEffect } from 'react';
import { apiService, fileToBase64 } from './services/apiService';
import { trackEvent } from './services/telemetry';
import ProcessingStage from './components/ProcessingStage';
import SubmittingStage from './components/SubmittingStage';
import SubmittedStage from './components/SubmittedStage';
import ErrorStage from './components/ErrorStage';
import UploadStage from './components/UploadStage';
import MyPOsDashboard from './components/MyPOsDashboard';
import ReviewStage from './components/review/ReviewStage';
import TotalMismatchModal from './components/review/TotalMismatchModal';
import { getNextBusinessDay, isPdfFile, isHtmlFile, isTextFile, deriveInItembal, getLineCosts, costFieldsFromRecheck } from './utils/formatting';
import { bulkPriceResolution, bulkPriceTotals, isBulkOutlier, BULK_PRICE_ORIGIN } from './utils/bulkResolve';
import { aplusOrderLine } from './utils/orderLine';

// Multi-PO upload only makes sense with the async pipeline on (see asyncEnabled
// below) — otherwise each file would drive its own synchronous review screen,
// one after another, which is worse than just uploading them one at a time.
const MAX_BATCH_FILES = 20;
const BATCH_UPLOAD_CONCURRENCY = 3; // Caps parallel uploadDocument calls — each does a
// blob write + PI session insert + get_next_order_number's ORHED retry loop; unbounded
// concurrency would hammer the order-number sequence under a big batch.

// text/html is included for EDI-rendered POs (e.g. Liaison EDINotepad) — verified
// 2026-09-08 that a real Smithfield PO in this format extracts cleanly via the same
// text-decode path .txt files already use. XML/JSON (2026-10-26): cXML orders from Ariba,
// which the backend reads directly; other XML/JSON goes to the model as text.
// CSV is left out on purpose: Windows browsers label .csv as application/vnd.ms-excel,
// which the backend would try to open as a spreadsheet.
function isValidPoFileType(file) {
  const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain', 'text/html',
                      'application/msword', 'application/vnd.ms-excel',
                      'text/xml', 'application/xml', 'application/json'];
  return validTypes.includes(file.type) || !!file.name.match(/\.(pdf|docx?|xlsx?|txt|html?|xml|cxml|json)$/i);
}

// Valid FOB codes for sales orders, per Atlantic's FOB Codes Reference (updated 2026-06-17).
// Hardcoded from the reference sheet — NOT sourced from vw_FOBCodes — so the dropdown only
// offers codes valid for a sales order. Excludes vendor-PO-only codes (MILL, IN, PU) and the
// PUNC/PUNM defaults the sheet says must never be manually selected.
const VALID_FOB_CODES = [
  { code: 'OT', description: 'Atlantic Truck' },
  { code: 'PPA', description: 'Prepay and Add' },
  { code: 'FFA', description: 'Freight/Freight Allow' },
  { code: 'CPU', description: 'Customer Pick Up' },
  { code: 'OTHER', description: 'Other / Collect' },
  { code: 'DELD', description: 'Delivered - No Freight' },
  { code: 'PP', description: 'Atlantic Pays Freight' },
];

// The manual FOB dropdown stays restricted to VALID_FOB_CODES above (the sales-order set).
// But a customer's real A+ default (SAFOBC/CMFOBC) can be a code outside that list
// (e.g. PU, MILL, PUNC). Rather than silently dropping it, surface it as an extra option
// — labeled with its real A+ description — so the default is honored and shown, not blanked.
const fobOptionsWithDefault = (selectedFobCode, apiFobCodes) => {
  const code = selectedFobCode?.code;
  if (!code || VALID_FOB_CODES.some(f => f.code === code)) return VALID_FOB_CODES;
  const match = (apiFobCodes || []).find(f => f.code === code);
  return [...VALID_FOB_CODES, { code, description: match?.description || 'Customer default (A+)' }];
};

// UOM equivalence check for stocking UOM validation
const FALLBACK_UOM_CANONICAL = {
  'RL': 'RL', 'ROL': 'RL', 'ROLL': 'RL',
  'BX': 'BX', 'BOX': 'BX',
  'EA': 'EA', 'EACH': 'EA', 'PC': 'EA', 'PCS': 'EA', 'UN': 'EA', 'UNIT': 'EA',
  'CS': 'CS', 'CASE': 'CS', 'CA': 'CS',
  'CT': 'CT', 'CTN': 'CT', 'CARTON': 'CT',
  'PK': 'PK', 'PKG': 'PK', 'PACK': 'PK', 'PACKAGE': 'PK',
  'DZ': 'DZ', 'DOZ': 'DZ', 'DOZEN': 'DZ',
  'FT': 'FT', 'FOOT': 'FT', 'FEET': 'FT',
  'IN': 'IN', 'INCH': 'IN', 'INCHES': 'IN',
  'LB': 'LB', 'LBS': 'LB', 'POUND': 'LB', 'POUNDS': 'LB',
  'OZ': 'OZ', 'OUNCE': 'OZ', 'OUNCES': 'OZ',
  'GAL': 'GAL', 'GALLON': 'GAL', 'GALLONS': 'GAL',
  'QT': 'QT', 'QUART': 'QT', 'QUARTS': 'QT',
  'PT': 'PT', 'PINT': 'PT', 'PINTS': 'PT',
  'M': 'M', 'THOUSAND': 'M', '1000 EA': 'M',
  // Skid/pallet are interchangeable for ordering (Tonya, 2026-11-02); A+ uses SK or PL.
  'SK': 'SK', 'SKID': 'SK', 'SKIDS': 'SK', 'SKD': 'SK', 'PL': 'SK', 'PLT': 'SK', 'PLTS': 'SK', 'PALLET': 'SK', 'PALLETS': 'SK',
};

function normalizeUom(uom) {
  if (!uom) return '';
  const upper = uom.trim().toUpperCase();
  return FALLBACK_UOM_CANONICAL[upper] || upper;
}

function isValidStockingUom(uom, item) {
  if (!uom || !item.unitOfMeasure) return true; // Can't validate without stocking UOMs
  const normalized = normalizeUom(uom);
  const stockingUoms = [item.unitOfMeasure, item.stockingUom2, item.stockingUom3]
    .filter(u => u && u.trim());
  if (stockingUoms.length === 0) return true;
  return stockingUoms.some(su => normalizeUom(su) === normalized);
}

// Stable per-line id: state updates and modals target line items by identity, not by
// array position, so a line deletion or an in-flight async validation can never misroute
// data onto the wrong line (fixes wrong-item-into-A+-after-delete).
const newLineId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `li-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;

// Numeric view of a PO line number, for ORDERING ONLY — never write this back onto an item.
// `poLineNumber` is the customer's own line number off their PO: it is sent as `lineNumber`,
// which the A+ delta map writes to XDELR# -> ODELR#, and those values flow out onto EDI
// invoices (proven on order AI0DO). It must therefore be treated as immutable customer data.
// Extraction types it as an int, but the DEV `?mock=1` fixture uses zero-padded strings, so
// coerce before comparing.
const poLineSeq = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ITEMBAL setup is tri-state (true / false / null = couldn't determine) — see deriveInItembal in
// utils/formatting.js. `null` must never collapse to `false`: false means a red badge and a hard
// submit block, so a swallowed ITBAL lookup failure would cost the CSR the order.

// Main App Component
export default function App() {
  const [stage, setStage] = useState('dashboard'); // dashboard, upload, processing, review, submitting, submitted, error
  const [isDragging, setIsDragging] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  // Gates multi-PO upload: only worth offering when every file in a batch will
  // actually be queued in the background rather than driving a synchronous
  // review screen one at a time. Defaults to false (single-file only) until
  // this resolves, so the UI never briefly offers something it can't support.
  const [asyncEnabled, setAsyncEnabled] = useState(false);

  useEffect(() => {
    apiService.getPipelineStatus()
      .then((res) => setAsyncEnabled(!!res.asyncEnabled))
      .catch(() => {}); // Stay single-file-only on failure — the safe default.
  }, []);

  // Fetch authenticated user info — redirect to login if not authenticated
  useEffect(() => {
    async function checkAuth() {
      try {
        const response = await fetch('/.auth/me');
        const data = await response.json();
        if (data.clientPrincipal) {
          setUser(data.clientPrincipal);
          setAuthChecked(true);
          return;
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
      // Not authenticated — redirect to AAD login, preserving current URL (including Outlook query params)
      window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(window.location.href)}`;
    }
    checkAuth();
  }, []);

  const [extractedData, setExtractedData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'pdf', 'office', 'text', or 'none'
  const [textContent, setTextContent] = useState(null);
  // Drives the processing screen's heading/copy: resuming an already-extracted PO
  // re-validates ship-to/items/pricing against current ERP data (deliberate — see
  // runPostExtractionPipeline), but that shouldn't read as "starting over" to the
  // CSR the way a brand-new upload's extraction screen does.
  const [isResuming, setIsResuming] = useState(false);
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [correlationId, setCorrelationId] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [error, setError] = useState(null);
  const [erpOrderNumber, setErpOrderNumber] = useState(null);
  // True when the backend recognized a repeat submit of an order already in A+ and sent
  // nothing (duplicate guard, 2026-10-02) — the Submitted screen says so.
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  // Multi-warehouse split result (backend, 2026-09-04): when line items ship from more
  // than one warehouse, submitOrder returns one entry per warehouse instead of a single
  // order number. Null for a normal single-warehouse order.
  const [submissionSplits, setSubmissionSplits] = useState(null);
  const [aiOrderNumber, setAiOrderNumber] = useState(null);
  const [extractionRetryData, setExtractionRetryData] = useState(null);

  // Selected values for editable fields
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedShipTo, setSelectedShipTo] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsRevalidating, setItemsRevalidating] = useState(false);
  const [itemRevalidationFailed, setItemRevalidationFailed] = useState(false);
  const [requestedShipDate, setRequestedShipDate] = useState(null);
  const [carrierCodes, setCarrierCodes] = useState([]);
  const [fobCodesFromApi, setFobCodesFromApi] = useState([]);
  const [selectedFobCode, setSelectedFobCode] = useState(null);
  const [selectedCarrierCode, setSelectedCarrierCode] = useState(null);
  const [warehouseCodes, setWarehouseCodes] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [totalMismatch, setTotalMismatch] = useState(null);
  const [shipVia, setShipVia] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  // Order-level comments — up to 4 lines, each with its own print code (see OrderComments.jsx)
  const [orderComments, setOrderComments] = useState([{ text: '', printCode: 'both' }]);
  const [headerDropShip, setHeaderDropShip] = useState(false);
  const [editedPoNumber, setEditedPoNumber] = useState(null);
  const [poNumberWarning, setPoNumberWarning] = useState(null);
  // Sales rep A+ assigns to the selected ship-to (ADDR.SARP#1) — display only
  const [salesRep, setSalesRep] = useState(null);

  // Dropdown states
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [shipToDropdownOpen, setShipToDropdownOpen] = useState(false);

  // Customer/Ship-To search modal state
  const [customerShipToSearchModal, setCustomerShipToSearchModal] = useState(null);

  const fileInputRef = useRef(null);
  const reviewStartedAtRef = useRef(null);
  const revalidationRef = useRef(0);

  // Clean up blob URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (filePreviewUrl && previewType === 'pdf') {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl, previewType]);

  // Look up the ship-to's sales rep whenever the customer/ship-to changes. Covers every way the
  // ship-to gets set (initial match, dropdown alternative, search modal). Stale responses from a
  // previous ship-to are dropped.
  const shipToCustomerNumber = selectedCustomer?.number;
  const shipToNumber = selectedShipTo?.number;
  useEffect(() => {
    setSalesRep(null);
    if (!shipToCustomerNumber || !shipToNumber) return;
    let cancelled = false;
    apiService.fetchOrderDefaults(String(shipToCustomerNumber), String(shipToNumber), correlationId)
      .then(result => { if (!cancelled) setSalesRep(result?.defaults?.sales_rep || null); })
      .catch(err => console.warn('Sales rep lookup failed:', err));
    return () => { cancelled = true; };
    // correlationId is only a log key; re-fetching when it changes would be redundant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipToCustomerNumber, shipToNumber]);

  const checkPODuplicate = useCallback(async (poNumber, customerNumber) => {
    if (!poNumber || !customerNumber) {
      setPoNumberWarning(null);
      return;
    }
    try {
      const result = await apiService.checkPONumber(poNumber, customerNumber, correlationId);
      setPoNumberWarning(result.exists ? result.message : null);
    } catch (err) {
      console.warn('PO duplicate check failed:', err);
      setPoNumberWarning(null);
    }
  }, [correlationId]);

  // Handle Outlook add-in handoff — file was pre-uploaded via the dialog bridge
  // Gated on authChecked: must not run before auth completes, otherwise it clears
  // URL params (via replaceState) before the auth redirect can capture them
  useEffect(() => {
    if (!authChecked) return;

    // Clean up stale Outlook localStorage entries (older than 30 minutes)
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('po-outlook-'))
        .forEach(k => {
          try {
            const data = JSON.parse(localStorage.getItem(k));
            if (data.timestamp && Date.now() - data.timestamp > 30 * 60 * 1000) {
              localStorage.removeItem(k);
            }
          } catch { localStorage.removeItem(k); }
        });
    } catch { /* ignore storage errors */ }

    const params = new URLSearchParams(window.location.search);
    if (params.get('from') !== 'outlook' || !params.get('cid')) return;

    const cid = params.get('cid');
    // Validate correlationId format (UUID)
    if (!/^[a-f0-9-]{36}$/i.test(cid)) return;

    function tryLoadFromLocalStorage() {
      const storageKey = `po-outlook-${cid}`;
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      let uploadData;
      try {
        uploadData = JSON.parse(stored);
      } catch {
        localStorage.removeItem(storageKey);
        return null;
      }

      if (!uploadData.correlationId || !uploadData.sasUrl || !uploadData.fileName) {
        localStorage.removeItem(storageKey);
        return null;
      }

      localStorage.removeItem(storageKey);
      return uploadData;
    }

    function tryLoadFromUrlParams() {
      const sasUrl = params.get('sasUrl');
      const fileName = params.get('fileName');
      if (!sasUrl || !fileName) return null;

      return {
        correlationId: cid,
        sasUrl,
        fileName,
        mimeType: params.get('mimeType') || 'application/octet-stream',
        officeViewerUrl: params.get('officeViewerUrl') || null,
        canUseOfficeViewer: params.get('canUseOfficeViewer') === '1',
        aiOrderNumber: params.get('aiOrderNumber') || null,
        asyncProcessing: params.get('asyncProcessing') === '1',
      };
    }

    function tryLoadAndProcess() {
      // Try localStorage first (works when opened in same browser context),
      // then fall back to URL params (works when opened in system browser via openBrowserWindow)
      const uploadData = tryLoadFromLocalStorage() || tryLoadFromUrlParams();
      if (!uploadData) return false;

      window.history.replaceState({}, '', '/');
      processOutlookUpload(uploadData);
      return true;
    }

    // Try immediately, then retry once after 500ms (race condition: dialog may still be writing to localStorage)
    if (!tryLoadAndProcess()) {
      setTimeout(() => {
        if (!tryLoadAndProcess()) {
          window.history.replaceState({}, '', '/');
        }
      }, 500);
    }
  }, [authChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sets filePreviewUrl/previewType/textContent from a {sasUrl, fileName, mimeType,
  // officeViewerUrl, canUseOfficeViewer} shape — the same fields uploadDocument's
  // response and getDocumentPreview's response both carry, so this covers both a
  // fresh upload (Outlook add-in) and re-fetching a preview for a PO resumed from
  // the dashboard, where the reviewing session never had the file bytes locally.
  async function applyDocumentPreview({ sasUrl, fileName, mimeType, officeViewerUrl, canUseOfficeViewer }) {
    if (canUseOfficeViewer && officeViewerUrl) {
      setFilePreviewUrl(officeViewerUrl);
      setPreviewType('office');
      setTextContent(null);
    } else if (isPdfFile(fileName, mimeType) && sasUrl) {
      // Fall back to the .pdf extension — some PDFs arrive with a wrong/generic MIME type
      // (e.g. Crystal Reports exports as application/octet-stream), which the backend and
      // upload validation already tolerate. Match that here so the preview still renders.
      setFilePreviewUrl(sasUrl);
      setPreviewType('pdf');
      setTextContent(null);
    } else if (isHtmlFile(fileName, mimeType) && sasUrl) {
      // HTML (EDI renders) - fetch and show in a sandboxed frame
      setFilePreviewUrl(null);
      setPreviewType('html');
      try {
        const htmlResponse = await fetch(sasUrl);
        if (!htmlResponse.ok) throw new Error('Failed to fetch HTML content');
        setTextContent(await htmlResponse.text());
      } catch {
        setTextContent(null);
        setPreviewType('none');
      }
    } else if (isTextFile(fileName, mimeType) && sasUrl) {
      // Text files - fetch and display inline
      setFilePreviewUrl(null);
      setPreviewType('text');
      try {
        const textResponse = await fetch(sasUrl);
        if (!textResponse.ok) throw new Error('Failed to fetch text content');
        setTextContent(await textResponse.text());
      } catch {
        setTextContent(null);
        setPreviewType('none');
      }
    } else {
      setFilePreviewUrl(null);
      setPreviewType('none');
      setTextContent(null);
    }
  }

  // Process a PO that was already uploaded via Outlook add-in
  async function processOutlookUpload({ correlationId: cid, sasUrl, fileName, mimeType, officeViewerUrl, canUseOfficeViewer, aiOrderNumber: outlookAiOrderNumber, asyncProcessing }) {
    trackEvent('po_upload_started', { fileName, fileType: mimeType, fromOutlook: true });
    setStage('processing');
    setIsResuming(false);
    setSubmitError(null);
    setProcessingStatus('Preparing document...');
    setCorrelationId(cid);
    setAiOrderNumber(outlookAiOrderNumber || null);
    setSelectedFile({ name: fileName, type: mimeType }); // Minimal file object for ProcessingStage display

    await applyDocumentPreview({ sasUrl, fileName, mimeType, officeViewerUrl, canUseOfficeViewer });

    // Same as handleFileSelect: the Outlook bridge's uploadDocument call already enqueued
    // this for background processing, so driving extractPO ourselves too would
    // double-process it. Hand off to the dashboard instead.
    if (asyncProcessing) {
      trackEvent('po_upload_queued_async', { correlationId: cid, fromOutlook: true });
      handleReset();
      return;
    }

    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';

    let currentStage = 'upload';
    const processStartTime = Date.now();
    let base64Content;

    try {
      // Download file from blob storage and convert to base64 for extraction
      setProcessingStatus('Downloading document...');
      const blobResponse = await fetch(sasUrl);
      if (!blobResponse.ok) throw new Error('Failed to download document from storage');
      const blob = await blobResponse.blob();
      base64Content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Extract PO data (OCR + LLM)
      currentStage = 'extract';
      setProcessingStatus('Extracting order data...');
      const extractStartTime = Date.now();
      const extractResult = await apiService.extractPOWithPolling(
        base64Content, fileName, mimeType, cid, userId, userEmail,
        () => setProcessingStatus('Extracting order data...')
      );
      trackEvent('po_extraction_complete', { correlationId: cid, durationMs: Date.now() - extractStartTime, itemCount: extractResult.po_data?.items?.length || 0 });
      const poData = extractResult.po_data;
      await runPostExtractionPipeline(poData, cid, userId, userEmail, processStartTime);
    } catch (err) {
      console.error('Processing error (Outlook handoff):', err);
      trackEvent('po_processing_error', { correlationId: cid, stage: currentStage, errorMessage: err.message });
      if (err.message?.includes('timed out')) {
        setExtractionRetryData({ base64Content, fileName, mimeType, correlationId: cid, userId, userEmail });
      }
      setError(err.message || 'Failed to process PO. Please try again.');
      setStage('error');
    }
  }

  // Build selectedItems array from item_validations response
  function buildSelectedItems(itemValidations) {
    return itemValidations.map((item, index) => ({
      _id: newLineId(),
      piLineIndex: index,
      extQuantityWasNull: item.po_quantity == null,
      poLineNumber: item.po_line_number || null,
      poItemNumber: item.po_item_number,
      poDescription: item.po_description,
      poQuantity: item.po_quantity,
      poUnitOfMeasure: item.po_unit_of_measure,
      selectedItemNumber: item.val_item_number,
      selectedDescription: item.val_description,
      unitOfMeasure: item.val_unit_of_measure,
      stockingUom2: item.val_unit_of_measure_2 || null,
      stockingUom3: item.val_unit_of_measure_3 || null,
      uomConversion1: item.val_uom_conversion_1 ?? null,
      uomConversion2: item.val_uom_conversion_2 ?? null,
      confidence: item.confidence,
      confidenceLevel: item.confidence_level,
      issue: item.issue,
      alternatives: item.alternatives || [],
      valUnitPrice: item.val_unit_price,
      valPriceUom: item.val_price_unit_of_measure,
      valPriceUomConversion: item.val_price_unit_conversion,
      // A+ set-up GL cost (ITBAL average cost) + the pricing U/M it's per — drives the
      // GL/OE Cost + M% display (read-only on stock lines, editable on drop-ship).
      aplusGlCost: item.aplus_gl_cost ?? null,
      costPricingUom: item.aplus_pricing_uom || null,
      costPricingPerStock: item.aplus_pricing_per_stock ?? null,
      priceValid: item.price_valid,
      priceVariance: item.price_variance,
      priceVarianceLevel: item.price_variance_level,
      priceIssue: item.price_issue,
      priceStockUomResolved: item.price_stock_uom_resolved || false,
      priceStockUnitPrice: item.price_stock_unit_price,
      priceStockUomLabel: item.price_stock_uom_label,
      inventory: item.inventory || null,
      inItembal: deriveInItembal(item.in_itembal, item.inventory),
      isDropShip: false,
      note: '',
      extQuantity: item.po_quantity,
      extUnitPrice: item.po_unit_price,
      // Set when the PO quoted a per-hundred/per-thousand price that the backend converted
      // to per-unit (Southwire "Price per Thousand", 2026-10-06) — shown on the PO line.
      extOriginalUnitPrice: item.po_original_unit_price ?? null,
      extOriginalPriceBasis: item.po_original_price_basis || null,
      // Set when the backend converted the PO's unit to the item's A+ unit (4,000 EA -> 4 M);
      // the PO's own quantity/unit/price, shown on the line and sent as the customer unit.
      poUomConverted: item.po_uom_converted || null,
      // The customer's A+ default U/M for this item (CDFUM), e.g. SK for Hebron 6210594: labels
      // lines ordered in it and is the unit the resolve dialog offers (Tonya, 2026-11-01).
      customerDefaultUom: item.customer_default_uom || null,
      extTotal: item.po_total,
      addedByCsr: false,
      // Full 1:1 UOM auto-convert: backend flags lines whose PO unit is a valid alternate that is
      // 1:1 interchangeable with a stocking unit (e.g. EACH for an item sold in CS). Auto-apply the
      // resolution so the line submits in the stocking unit with no "Resolve Stocking UOM" step.
      resolution: item.uom_auto_convert ? {
        origin: `1:1 UOM (${(item.po_unit_of_measure || '').toUpperCase()}→${item.uom_auto_convert.to})`,
        type: 'uom_auto_1to1',
        quantity: item.po_quantity, // 1:1 conversion — quantity unchanged
        uom: item.uom_auto_convert.to,
        unitPrice: item.uom_auto_convert.unit_price != null
          ? item.uom_auto_convert.unit_price
          : item.val_unit_price,
      } : undefined,
    }));
  }

  // Shared post-extraction pipeline: validateShipTo → validateItems + orderDefaults → set state → review
  async function runPostExtractionPipeline(poData, cid, userId, userEmail, startTime, resuming = false) {
    setExtractedData(poData);
    // Jeff (2026-07-20): never seed a past requested-ship date. If the PO's requested
    // date is before the earliest selectable date (next business day, same floor the
    // picker enforces), ignore it and leave the field empty so the CSR must pick a valid
    // date — otherwise a past date loads that the user then can't re-select. No PO date
    // at all still defaults to the next business day.
    const minShipDate = getNextBusinessDay();
    const poShipDate = poData.requested_delivery_date;
    setRequestedShipDate(
      !poShipDate ? minShipDate : (poShipDate >= minShipDate ? poShipDate : null)
    );

    // Validate ship-to address
    setProcessingStatus(resuming ? 'Refreshing ship-to address...' : 'Validating ship-to address...');
    const shiptoResult = await apiService.validateShipTo(poData, cid, userId, userEmail);
    const shiptoValidations = shiptoResult.shipto_validations;

    if (shiptoValidations) {
      setSelectedShipTo({
        name: shiptoValidations.val_shipto_name,
        number: shiptoValidations.val_shipto_number,
        address: shiptoValidations.val_shipto_address,
        address2: shiptoValidations.val_shipto_address2,
        city: shiptoValidations.val_shipto_city,
        state: shiptoValidations.val_shipto_state,
        zip: shiptoValidations.val_shipto_zip,
        customerNumber: shiptoValidations.val_shipto_customer_number,
        confidence: shiptoValidations.shipto_confidence_score
      });
      setSelectedCustomer({
        name: shiptoValidations.val_shipto_customer_name,
        number: shiptoValidations.val_shipto_customer_number,
        confidence: shiptoValidations.shipto_confidence_score
      });
    }

    // Validate items and fetch order defaults in parallel
    setProcessingStatus(resuming ? 'Checking current pricing and stock...' : 'Validating items and pricing...');
    const customerNumber = shiptoValidations?.val_shipto_customer_number || null;
    const shiptoNumber = shiptoValidations?.val_shipto_number || null;
    const shiptoConfidence = shiptoValidations?.shipto_confidence_score || 0.0;
    const [itemsResult, defaultsResult] = await Promise.all([
      apiService.validateItems(
        poData.items || [], customerNumber, shiptoNumber, shiptoConfidence,
        cid, userId, userEmail
      ),
      apiService.fetchOrderDefaults(customerNumber, shiptoNumber, cid).catch(err => {
        console.warn('Order defaults fetch failed (non-blocking):', err);
        return null;
      }),
    ]);

    // Apply order defaults (FOB/Carrier)
    if (defaultsResult) {
      setCarrierCodes(defaultsResult.carrier_codes || []);
      setFobCodesFromApi(defaultsResult.fob_codes || []);
      setWarehouseCodes(defaultsResult.warehouse_codes || []);
      const defaults = defaultsResult.defaults || {};
      // Honor the customer's actual A+ default even if it's outside the curated dropdown list.
      if (defaults.fob_code) {
        setSelectedFobCode({ code: defaults.fob_code, source: defaults.fob_source });
      }
      if (defaults.carrier_code) {
        setSelectedCarrierCode({ code: defaults.carrier_code, source: defaults.carrier_source });
      }
      if (defaults.warehouse_code) {
        setSelectedWarehouse({ code: defaults.warehouse_code, source: defaults.warehouse_source });
      } else {
        setSelectedWarehouse(null);
      }
      if (defaults.ship_via) {
        setShipVia(defaults.ship_via);
      }
    }

    const validation = {
      shipto_validations: shiptoValidations,
      item_validations: itemsResult.item_validations || [],
      overall_confidence: itemsResult.overall_confidence || 'low',
      validation_issues: itemsResult.validation_issues || []
    };
    setValidationResult(validation);

    if (validation.item_validations) {
      setSelectedItems(buildSelectedItems(validation.item_validations));
    }

    trackEvent('po_validation_complete', { correlationId: cid, totalDurationMs: Date.now() - startTime, itemCount: poData.items?.length || 0 });
    reviewStartedAtRef.current = Date.now();
    setStage('review');

    // Check for duplicate PO number on review load
    const poNum = poData.po_number;
    const custNum = shiptoValidations?.val_shipto_customer_number;
    if (poNum && custNum) {
      apiService.checkPONumber(poNum, custNum, cid)
        .then(result => {
          if (result.exists) setPoNumberWarning(result.message);
        })
        .catch(err => console.warn('PO duplicate check failed:', err));
    }
  }

  // Resume a PO from the "My POs" dashboard — a background job (processPO, the async
  // pipeline) already ran extraction, so this re-fetches that result (via the same
  // extractPOStatus endpoint extractPO's own poll path uses — processPO caches its
  // result there too, specifically so this resume flow doesn't need a separate backend
  // endpoint) and re-runs validation against CURRENT master data rather than replaying a
  // stale snapshot — desirable given how long a PO can sit in "ready for review" before
  // a CSR gets to it (inventory/pricing can drift in the meantime).
  async function handleResumeFromDashboard(cid, aiOrderNumberFromRow) {
    trackEvent('po_dashboard_resume_started', { correlationId: cid });
    setStage('processing');
    setIsResuming(true);
    setProcessingStatus('Loading order...');
    setCorrelationId(cid);
    setAiOrderNumber(aiOrderNumberFromRow || null);
    setFilePreviewUrl(null);
    setPreviewType('none');
    setTextContent(null);

    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';
    const startTime = Date.now();

    try {
      const statusResult = await apiService.pollExtractPOStatus(cid);
      if (statusResult.status !== 'completed' || !statusResult.po_data) {
        throw new Error('This order is not ready for review yet.');
      }
      setSelectedFile({ name: statusResult.file_name || 'document', type: '' });

      // Best-effort: a missing/failed preview shouldn't block resuming the
      // review itself, so this is fire-and-forget rather than awaited into
      // the try/catch that drives the resume's success/failure path.
      apiService.fetchDocumentPreview(cid)
        .then((preview) => applyDocumentPreview(preview))
        .catch((err) => console.warn('Document preview unavailable:', err.message));

      await runPostExtractionPipeline(statusResult.po_data, cid, userId, userEmail, startTime, true);
    } catch (err) {
      console.error('Resume error:', err);
      trackEvent('po_processing_error', { correlationId: cid, stage: 'resume', errorMessage: err.message });
      setError(err.message || 'Failed to load this order. Please try again from your dashboard.');
      setStage('error');
    }
  }

  // Handle file selection
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    if (!isValidPoFileType(file)) {
      setError('Please upload a PDF, Word, Excel, HTML, text, XML, or JSON file.');
      return;
    }

    setSelectedFile(file);
    setSubmitError(null);
    setError(null);
    // Clear the whole-order drop-ship toggle so a fresh PO doesn't inherit the
    // previous order's value (it's a manual CSR toggle, never set from extraction).
    // Covers the "process a new PO" entry point independently of handleReset.
    setHeaderDropShip(false);
    setStage('processing');
    setIsResuming(false);
    setProcessingStatus('Uploading document to storage...');

    // Get user info for audit
    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';

    trackEvent('po_upload_started', { fileName: file.name, fileType: file.type, fileSize: file.size, fromOutlook: false });

    // Convert file to base64 once — reused by upload and extract
    const base64Content = await fileToBase64(file);

    let currentStage = 'upload';
    const uploadStartTime = Date.now();
    let newCorrelationId = null;

    try {
      // Step 1: Upload document to blob storage
      const uploadResult = await apiService.uploadDocument(base64Content, file.name, file.type, userId, userEmail);
      newCorrelationId = uploadResult.correlationId;
      setCorrelationId(newCorrelationId);
      setAiOrderNumber(uploadResult.aiOrderNumber);
      trackEvent('po_upload_complete', { correlationId: newCorrelationId, aiOrderNumber: uploadResult.aiOrderNumber, durationMs: Date.now() - uploadStartTime });

      // Revoke previous blob URL if one exists
      if (filePreviewUrl && previewType === 'pdf') {
        URL.revokeObjectURL(filePreviewUrl);
      }

      // Set up preview based on file type
      if (isPdfFile(file.name, file.type)) {
        // PDF files can use a local blob URL. Some PDFs arrive with a wrong/empty MIME type
        // (e.g. Crystal Reports exports); force the blob's content-type to application/pdf
        // so the preview iframe renders it instead of trying to download it.
        const pdfBlob = file.type === 'application/pdf' ? file : file.slice(0, file.size, 'application/pdf');
        setFilePreviewUrl(URL.createObjectURL(pdfBlob));
        setPreviewType('pdf');
        setTextContent(null);
      } else if (isHtmlFile(file.name, file.type)) {
        // HTML (EDI renders) - read content for the sandboxed preview frame
        setFilePreviewUrl(null);
        setPreviewType('html');
        try {
          setTextContent(await file.text());
        } catch {
          setTextContent(null);
          setPreviewType('none');
        }
      } else if (isTextFile(file.name, file.type)) {
        // Text files - read content for inline display
        setFilePreviewUrl(null);
        setPreviewType('text');
        try {
          setTextContent(await file.text());
        } catch {
          setTextContent(null);
          setPreviewType('none');
        }
      } else if (uploadResult.canUseOfficeViewer && uploadResult.officeViewerUrl) {
        // Word/Excel files use Office Online viewer
        setFilePreviewUrl(uploadResult.officeViewerUrl);
        setPreviewType('office');
        setTextContent(null);
      } else {
        // Other files - no preview
        setFilePreviewUrl(null);
        setPreviewType('none');
        setTextContent(null);
      }

      // Backend enqueued this for background processing (ASYNC_PIPELINE_ENABLED) -
      // processPO will run extraction+validation itself and email the CSR when it's
      // ready for review. Driving extractPO ourselves too would double-process the
      // same PO (see uploadDocument's own comment on asyncProcessing) - hand off to
      // the dashboard instead, where the CSR will see it land as "ready for review".
      if (uploadResult.asyncProcessing) {
        trackEvent('po_upload_queued_async', { correlationId: newCorrelationId });
        handleReset();
        return;
      }

      // Step 2: Extract PO data (OCR + LLM)
      currentStage = 'extract';
      setProcessingStatus('Extracting order data...');
      const extractStartTime = Date.now();
      const extractResult = await apiService.extractPOWithPolling(
        base64Content, file.name, file.type, newCorrelationId, userId, userEmail,
        () => setProcessingStatus('Extracting order data...')
      );
      trackEvent('po_extraction_complete', { correlationId: newCorrelationId, durationMs: Date.now() - extractStartTime, itemCount: extractResult.po_data?.items?.length || 0 });
      const poData = extractResult.po_data;
      await runPostExtractionPipeline(poData, newCorrelationId, userId, userEmail, uploadStartTime);
    } catch (err) {
      console.error('Processing error:', err);
      trackEvent('po_processing_error', { correlationId: newCorrelationId, stage: currentStage, errorMessage: err.message });
      if (err.message?.includes('timed out')) {
        setExtractionRetryData({ base64Content, fileName: file.name, mimeType: file.type, correlationId: newCorrelationId, userId, userEmail });
      }
      setError(err.message || 'Failed to process PO. Please try again.');
      setStage('error');
    }
  }, [user, filePreviewUrl, previewType]);

  // Upload several POs at once — only offered when asyncEnabled (see its own
  // state declaration), since every file needs to land in the background
  // queue rather than driving its own synchronous review screen. Rejects the
  // whole selection up front on a bad file type or too many files, rather
  // than silently dropping some and proceeding with the rest.
  const handleBatchUpload = useCallback(async (files) => {
    if (files.length > MAX_BATCH_FILES) {
      setError(`Please select ${MAX_BATCH_FILES} files or fewer at a time (selected ${files.length}).`);
      return;
    }
    const invalidNames = files.filter((f) => !isValidPoFileType(f)).map((f) => f.name);
    if (invalidNames.length > 0) {
      setError(`These files aren't a supported type (PDF, Word, Excel, HTML, text, XML, or JSON): ${invalidNames.join(', ')}`);
      return;
    }

    setSubmitError(null);
    setError(null);
    setHeaderDropShip(false);
    setStage('processing');
    setIsResuming(false);
    setIsBatchUploading(true);
    setSelectedFile({ name: `${files.length} PO${files.length === 1 ? '' : 's'}`, type: '' });

    trackEvent('po_batch_upload_started', { fileCount: files.length });

    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';
    const startTime = Date.now();

    let completed = 0;
    let queued = 0;
    const failedNames = [];
    let nextIndex = 0;
    setProcessingStatus(`Uploading 0 of ${files.length}...`);

    async function worker() {
      while (nextIndex < files.length) {
        const file = files[nextIndex++];
        try {
          const base64Content = await fileToBase64(file);
          const uploadResult = await apiService.uploadDocument(base64Content, file.name, file.type, userId, userEmail);
          if (uploadResult.asyncProcessing) {
            queued++;
          } else {
            // Backend fell back to sync for this one file (e.g. the queue enqueue
            // itself failed) — it's uploaded and has a session, but nothing will
            // finish it automatically. Rare, and not worth building a mid-batch
            // synchronous-review detour for; logged so it's not silently lost.
            failedNames.push(file.name);
          }
        } catch (err) {
          console.error('Batch upload failed for', file.name, err);
          failedNames.push(file.name);
        }
        completed++;
        setProcessingStatus(`Uploading ${completed} of ${files.length}...`);
      }
    }

    await Promise.all(
      Array.from({ length: Math.min(BATCH_UPLOAD_CONCURRENCY, files.length) }, worker)
    );

    trackEvent('po_batch_upload_complete', {
      total: files.length, queued, failed: failedNames.length, durationMs: Date.now() - startTime,
    });
    setIsBatchUploading(false);

    if (queued > 0) {
      if (failedNames.length > 0) {
        console.warn('Batch upload: not queued for background processing:', failedNames);
      }
      // Land on the dashboard — the queued POs will show up there as they finish.
      handleReset();
    } else {
      setStage('upload');
      setError(`Failed to queue any of the ${files.length} file${files.length === 1 ? '' : 's'} for processing. Please try again.`);
    }
  }, [user]);

  // Handle drag and drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    if (files.length > 1 && asyncEnabled) {
      handleBatchUpload(files);
    } else {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect, handleBatchUpload, asyncEnabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle file input change
  const handleInputChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 1 && asyncEnabled) {
      handleBatchUpload(files);
    } else {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect, handleBatchUpload, asyncEnabled]);

  // Handle item selection change (from alternatives dropdown or manual search)
  const updateItemSelection = useCallback(async (index, newItem, searchQuery) => {
    const needsPriceValidation = !('price_valid' in newItem);
    const isManualSearch = needsPriceValidation;
    // Target this line by stable id, not array index — the array can shift (a delete, or
    // the customer re-validation rebuilding it) while the async price validation is in flight.
    const targetId = selectedItems[index]?._id;

    // Log action to Processing Intelligence
    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';
    if (correlationId) {
      const prevItem = selectedItems[index];
      apiService.logAction(correlationId, userId, userEmail, {
        type: isManualSearch ? 'search_select_item' : 'select_alternative',
        lineIndex: prevItem?.piLineIndex ?? index,
        previousValue: prevItem?.selectedItemNumber || null,
        newValue: newItem.item_number,
        searchQuery: searchQuery || null,
        confidenceBefore: prevItem?.confidence,
        confidenceAfter: 1.0,
      });
    }

    // Immediately update item state (instant UI feedback)
    setSelectedItems(prev => prev.map(item =>
      item._id === targetId ? {
        ...item,
        wasManualSearch: isManualSearch,
        customerDefaultUom: null,
        selectedItemNumber: newItem.item_number,
        selectedDescription: newItem.description,
        unitOfMeasure: newItem.unit_of_measure,
        stockingUom2: newItem.unit_of_measure_2 || null,
        stockingUom3: newItem.unit_of_measure_3 || null,
        uomConversion1: newItem.uom_conversion_1 ?? null,
        uomConversion2: newItem.uom_conversion_2 ?? null,
        confidence: 1.0,
        confidenceLevel: 'high',
        issue: null,
        inventory: newItem.inventory || null,
        inItembal: deriveInItembal(newItem.in_itembal, newItem.inventory),
        valUnitPrice: newItem.val_unit_price ?? null,
        valPriceUom: newItem.val_price_unit_of_measure,
        valPriceUomConversion: newItem.val_price_unit_conversion,
        priceValid: newItem.price_valid ?? null,
        priceVariance: newItem.price_variance ?? null,
        priceVarianceLevel: newItem.price_variance_level ?? null,
        priceIssue: needsPriceValidation ? 'Validating pricing...' : (newItem.price_issue ?? null),
        priceStockUomResolved: newItem.price_stock_uom_resolved || false,
        priceStockUnitPrice: newItem.price_stock_unit_price,
        priceStockUomLabel: newItem.price_stock_uom_label,
        resolution: null, // Clear previous resolution when switching items
        // A+ set-up cost of the NEW item (refreshed below by the warehouse recheck); a cost the
        // CSR typed for the old item doesn't apply to this one.
        aplusGlCost: newItem.aplus_gl_cost ?? null,
        costPricingUom: newItem.aplus_pricing_uom || null,
        costPricingPerStock: newItem.aplus_pricing_per_stock ?? null,
        glCost: undefined,
        oeCost: undefined,
      } : item
    ));

    // If no price data (e.g. from manual search), run async price validation
    if (needsPriceValidation && newItem.item_number) {
      const customerNumber = selectedCustomer?.number;
      const prevItem = selectedItems[index];
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';

      try {
        const priceResult = await apiService.validatePrice(
          newItem.item_number,
          customerNumber,
          prevItem?.poQuantity ?? prevItem?.extQuantity ?? 1,
          prevItem?.extUnitPrice,
          prevItem?.poUnitOfMeasure,
          newItem.unit_of_measure,
          correlationId,
          userId,
          userEmail,
          prevItem?.warehouseId || selectedWarehouse?.code || null
        );

        setSelectedItems(prev => prev.map(item =>
          item._id === targetId ? {
            ...item,
            valUnitPrice: priceResult.val_unit_price,
            valPriceUom: priceResult.val_price_unit_of_measure,
            valPriceUomConversion: priceResult.val_price_unit_conversion,
            priceValid: priceResult.price_valid,
            priceVariance: priceResult.price_variance,
            priceVarianceLevel: priceResult.price_variance_level,
            priceIssue: priceResult.price_issue,
            priceStockUomResolved: priceResult.price_stock_uom_resolved || false,
            priceStockUnitPrice: priceResult.price_stock_unit_price,
            priceStockUomLabel: priceResult.price_stock_uom_label,
          } : item
        ));
      } catch (err) {
        console.error('Price validation failed:', err);
        setSelectedItems(prev => prev.map(item =>
          item._id === targetId ? {
            ...item,
            priceIssue: 'Price validation failed',
            priceVarianceLevel: null,
          } : item
        ));
      }
    }

    // Re-check ITEMBAL/inventory for the NEW item at the currently selected warehouse. A
    // searched/alternative item carries no inventory for this warehouse, so without this the
    // line kept showing "Not set up in ITEMBAL" even when the new item IS stocked there
    // (Tonya, 2026-07-23: "OP did not update once the item# changed"). Mirrors the
    // warehouse-change recheck; non-blocking.
    const invWarehouse = selectedItems.find(it => it._id === targetId)?.warehouseId || selectedWarehouse?.code;
    if (invWarehouse && newItem.item_number) {
      try {
        const invResult = await apiService.recheckInventory(invWarehouse, [newItem.item_number], correlationId);
        const inv = (invResult.inventory || {})[newItem.item_number] || null;
        const inItembal = deriveInItembal((invResult.in_itembal || {})[newItem.item_number], inv);
        setSelectedItems(prev => prev.map(item =>
          item._id === targetId ? { ...item, inventory: inv, inItembal, ...costFieldsFromRecheck(invResult.costs, newItem.item_number) } : item
        ));
      } catch (err) {
        console.warn('Inventory recheck after item change failed (non-blocking):', err);
      }
    }
  }, [selectedCustomer, correlationId, user, selectedItems, selectedWarehouse]);

  // b1: CSR confirms a description-matched item is correct, clearing the confirm-item submit gate.
  // Targeted by stable `_id` (not array index) — the array can shift under an in-flight
  // re-validation or a delete. Note `itemConfirmed` is intentionally NOT preserved by
  // revalidateForCustomerChange: item matching is customer-keyed (IAXRF), so a new customer
  // re-matches the line and the CSR must confirm the new guess.
  // `confirmed = false` is the CSR undoing a misclick, which re-arms the submit gate.
  const updateItemConfirmed = useCallback((lineId, confirmed = true) => {
    setSelectedItems(prev => prev.map(item =>
      item._id === lineId ? { ...item, itemConfirmed: confirmed } : item
    ));
    const it = selectedItems.find(item => item._id === lineId);
    trackEvent(confirmed ? 'confirm_item_clicked' : 'confirm_item_undone', {
      correlationId,
      itemNumber: it?.selectedItemNumber || null,
    });
    if (correlationId) {
      apiService.logAction(correlationId, user?.userId || 'unknown', user?.userDetails || 'unknown', {
        type: confirmed ? 'confirm_description_match' : 'unconfirm_description_match',
        lineIndex: it?.piLineIndex ?? null,
        newValue: it?.selectedItemNumber || null,
      });
    }
  }, [selectedItems, correlationId, user]);

  // Validate price for a single item (used after quantity edit or independently)
  const validatePriceForItem = useCallback(async (index, overrides = {}) => {
    const item = selectedItems[index];
    if (!item?.selectedItemNumber) return;
    const targetId = item._id;

    const customerNumber = selectedCustomer?.number;
    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';

    // Save previous resolution price for added lines (needed if no contract price). On the
    // ship-to/customer re-validation path the resolution is nulled (see the buildSelectedItems
    // re-insert) BEFORE this runs, so item.resolution is already gone — fall back to
    // _csrAddedPrice so the CSR's entered price is restored instead of stranding the line.
    const prevResolutionPrice = item.addedByCsr
      ? (item.resolution?.unitPrice ?? item._csrAddedPrice ?? null)
      : null;

    // Clear stale resolution and set loading state
    setSelectedItems(prev => prev.map(it =>
      it._id === targetId ? {
        ...it,
        resolution: null,
        priceIssue: 'Validating pricing...',
        priceVarianceLevel: null,
      } : it
    ));

    // The new item's customer default U/M (CDFUM), for the label and the resolve dialog.
    // Non-blocking; applied only if the line still holds this item when it comes back.
    if (newItem.item_number && selectedCustomer?.number) {
      apiService.getItemUoms(newItem.item_number, selectedCustomer.number)
        .then(data => setSelectedItems(prev => prev.map(it =>
          it._id === targetId && it.selectedItemNumber === newItem.item_number
            ? { ...it, customerDefaultUom: data.customer_default_uom || null } : it)))
        .catch(() => {});
    }

    try {
      const priceResult = await apiService.validatePrice(
        item.selectedItemNumber,
        customerNumber,
        overrides.quantity ?? item.poQuantity ?? item.extQuantity ?? 1,
        item.extUnitPrice,
        item.poUnitOfMeasure,
        item.unitOfMeasure,
        correlationId,
        userId,
        userEmail,
        item.warehouseId || selectedWarehouse?.code || null
      );

      setSelectedItems(prev => prev.map(it => {
        if (it._id !== targetId) return it;

        const updated = {
          ...it,
          valUnitPrice: priceResult.val_unit_price,
          valPriceUom: priceResult.val_price_unit_of_measure,
          valPriceUomConversion: priceResult.val_price_unit_conversion,
          priceValid: priceResult.price_valid,
          priceVariance: priceResult.price_variance,
          priceVarianceLevel: priceResult.price_variance_level,
          priceIssue: priceResult.price_issue,
          priceStockUomResolved: priceResult.price_stock_uom_resolved || false,
          priceStockUnitPrice: priceResult.price_stock_unit_price,
          priceStockUomLabel: priceResult.price_stock_uom_label,
        };

        // Auto-resolve for CSR-added lines (they have no extUnitPrice fallback)
        if (it.addedByCsr) {
          const hasUomMismatch = priceResult.price_issue?.includes('UOM Mismatch') || false;
          if (!hasUomMismatch) {
            let autoPrice = null;
            if (priceResult.val_unit_price != null) {
              const conv = priceResult.val_price_unit_conversion;
              const pUom = (priceResult.val_price_unit_of_measure || '').toUpperCase();
              const sUom = (it.unitOfMeasure || '').toUpperCase();
              if (conv && conv > 0 && pUom !== sUom) {
                autoPrice = priceResult.val_unit_price * conv;
              } else {
                autoPrice = priceResult.val_unit_price;
              }
            } else {
              // No contract price — preserve previous resolution's price
              autoPrice = prevResolutionPrice ?? null;
            }

            if (autoPrice != null) {
              updated.resolution = {
                origin: 'csr_added',
                type: 'csr_added',
                quantity: it.poQuantity,
                uom: it.unitOfMeasure,
                unitPrice: autoPrice,
              };
            }
          }
          // If UOM mismatch, leave resolution null — PriceResolutionModal handles it
        }

        return updated;
      }));
    } catch (err) {
      console.error('Price validation failed:', err);
      setSelectedItems(prev => prev.map(it =>
        it._id === targetId ? {
          ...it,
          priceIssue: 'Price validation failed',
          priceVarianceLevel: null,
        } : it
      ));
    }
  }, [selectedCustomer, correlationId, user, selectedItems, selectedWarehouse]);

  // Resolve price/UOM issue (stores resolution separately, original data stays intact)
  const resolvePrice = useCallback((index, resolution) => {
    trackEvent('csr_price_resolved', { correlationId, resolutionType: resolution.type });
    const targetId = selectedItems[index]?._id;
    // Log action to Processing Intelligence
    if (correlationId) {
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';
      const item = selectedItems[index];
      apiService.logAction(correlationId, userId, userEmail, {
        type: 'resolve_price',
        lineIndex: item?.piLineIndex ?? index,
        newValue: resolution.origin,
        resolutionType: resolution.type,
      });
    }
    setSelectedItems(prev => prev.map(item =>
      item._id === targetId
        ? {
            ...item,
            resolution,
            // Keep the added-line price fallback in sync with the latest manual price so a
            // later re-validation restores THIS price, not the original add-time one.
            ...(item.addedByCsr && resolution?.unitPrice != null
              ? { _csrAddedPrice: resolution.unitPrice }
              : {}),
          }
        : item
    ));
  }, [correlationId, user, selectedItems]);

  // Resolve every listed price-difference line the same way at once ('po' keeps the PO's
  // prices, 'contract' takes A+'s). Same resolution per line as the modal; see bulkResolve.js.
  const resolvePricesBulk = useCallback((choice, itemIds) => {
    const ids = new Set(itemIds);
    const targets = selectedItems.filter(item => ids.has(item._id));
    if (targets.length === 0) return;
    trackEvent('csr_price_resolved_bulk', { correlationId, choice, lineCount: targets.length });
    if (correlationId) {
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';
      targets.forEach((item) => {
        apiService.logAction(correlationId, userId, userEmail, {
          type: 'resolve_price',
          lineIndex: item.piLineIndex ?? selectedItems.indexOf(item),
          newValue: BULK_PRICE_ORIGIN,
          resolutionType: choice === 'po' ? 'po' : 'contract',
        });
      });
    }
    setSelectedItems(prev => prev.map(item =>
      ids.has(item._id) ? { ...item, resolution: bulkPriceResolution(item, choice) } : item
    ));
  }, [correlationId, user, selectedItems]);

  // Delete a line item from the order
  const deleteLineItem = useCallback((index) => {
    const targetId = selectedItems[index]?._id;
    // Log action to Processing Intelligence
    if (correlationId) {
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';
      const item = selectedItems[index];
      apiService.logAction(correlationId, userId, userEmail, {
        type: 'delete_line',
        lineIndex: item?.piLineIndex ?? index,
        previousValue: item?.selectedItemNumber,
      });
    }
    setSelectedItems(prev => prev.filter(item => item._id !== targetId));
  }, [correlationId, user, selectedItems]);

  // Update quantity for a line item (null entry or edit of existing)
  const updateItemQuantity = useCallback((index, quantity) => {
    // Log action to Processing Intelligence
    if (correlationId) {
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';
      const item = selectedItems[index];
      const originalQty = item.extQuantity;
      const isEdit = originalQty != null;
      apiService.logAction(correlationId, userId, userEmail, {
        type: isEdit ? 'edit_quantity' : 'enter_quantity',
        lineIndex: item?.piLineIndex ?? index,
        ...(isEdit && { previousValue: String(originalQty) }),
        newValue: String(quantity),
      });
    }
    setSelectedItems(prev => prev.map((item, i) =>
      i === index ? { ...item, poQuantity: quantity } : item
    ));
  }, [correlationId, user, selectedItems]);

  // Re-validate all items when customer changes — runs PO check, order defaults, and item validation in parallel
  const revalidateForCustomerChange = useCallback(async (newCustomerNumber, newShipToNumber) => {
    const thisRevalidation = ++revalidationRef.current;
    setItemsRevalidating(true);
    setItemRevalidationFailed(false);
    // Preserve CSR-added lines — they'll be re-inserted after re-validation
    const addedLines = selectedItems.filter(item => item.addedByCsr);
    setSelectedItems([]);
    setValidationResult(prev => prev ? {
      ...prev,
      item_validations: [],
      validation_issues: [],
    } : prev);

    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';
    const poNum = editedPoNumber ?? extractedData?.po_number;

    try {
      const [itemsResult, , ] = await Promise.all([
        apiService.validateItems(
          extractedData?.items || [], newCustomerNumber, newShipToNumber, 1.0,
          correlationId, userId, userEmail
        ),
        apiService.fetchOrderDefaults(newCustomerNumber, newShipToNumber, correlationId)
          .then(result => {
            if (result) {
              setCarrierCodes(result.carrier_codes || []);
              setFobCodesFromApi(result.fob_codes || []);
              setWarehouseCodes(result.warehouse_codes || []);
              const defaults = result.defaults || {};
              if (defaults.fob_code) {
                setSelectedFobCode({ code: defaults.fob_code, source: defaults.fob_source });
              } else {
                setSelectedFobCode(null);
              }
              if (defaults.carrier_code) {
                setSelectedCarrierCode({ code: defaults.carrier_code, source: defaults.carrier_source });
              } else {
                setSelectedCarrierCode(null);
              }
              if (defaults.warehouse_code) {
                setSelectedWarehouse({ code: defaults.warehouse_code, source: defaults.warehouse_source });
              } else {
                setSelectedWarehouse(null);
              }
              if (defaults.ship_via) {
                setShipVia(defaults.ship_via);
              }
            }
          })
          .catch(err => console.warn('Order defaults re-fetch failed:', err)),
        poNum && newCustomerNumber
          ? apiService.checkPONumber(poNum, newCustomerNumber, correlationId)
              .then(result => setPoNumberWarning(result.exists ? result.message : null))
              .catch(err => { console.warn('PO duplicate check failed:', err); setPoNumberWarning(null); })
          : Promise.resolve(),
      ]);

      // Guard against stale response from a previous revalidation
      if (revalidationRef.current !== thisRevalidation) return;

      const newItemValidations = itemsResult.item_validations || [];
      setValidationResult(prev => ({
        ...prev,
        item_validations: newItemValidations,
        overall_confidence: itemsResult.overall_confidence || 'low',
        validation_issues: itemsResult.validation_issues || [],
      }));
      // Rebuild selected items from validation, then re-insert any CSR-added lines
      const rebuilt = buildSelectedItems(newItemValidations);

      if (addedLines.length > 0) {
        let merged = [...rebuilt];
        for (const addedLine of addedLines) {
          const ln = poLineSeq(addedLine.poLineNumber);
          // Position the re-inserted line WITHOUT renumbering anything (see poLineSeq): the old
          // conflict-bump rewrote the customer's real line numbers on every line at or after
          // the insert point, and those values reach outbound EDI invoices.
          let insertIdx = merged.length;
          for (let i = 0; i < merged.length; i++) {
            if (poLineSeq(merged[i].poLineNumber) > ln) { insertIdx = i; break; }
          }
          // Clear resolution (pricing is customer-specific) and mark for re-validation
          merged.splice(insertIdx, 0, { ...addedLine, resolution: null, priceIssue: 'Validating pricing...' });
        }
        setSelectedItems(merged);

        // Async re-validate pricing for each added line with new customer
        for (let i = 0; i < merged.length; i++) {
          if (merged[i].addedByCsr) {
            setTimeout(() => validatePriceForItem(i), 100);
          }
        }
      } else {
        setSelectedItems(rebuilt);
      }
    } catch (err) {
      console.error('Item re-validation failed:', err);
      // Guard against stale response
      if (revalidationRef.current !== thisRevalidation) return;
      setItemRevalidationFailed(true);
    } finally {
      if (revalidationRef.current === thisRevalidation) {
        setItemsRevalidating(false);
      }
    }
  }, [correlationId, user, extractedData, editedPoNumber, selectedItems, validatePriceForItem]);

  // Handle customer/ship-to search selection
  const handleCustomerShipToSearchSelect = useCallback((customer, shipTo) => {
    // Log actions to Processing Intelligence
    if (correlationId) {
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';
      apiService.logAction(correlationId, userId, userEmail, {
        type: 'select_customer',
        previousValue: selectedCustomer?.number,
        newValue: customer.customer_number,
        confidenceBefore: selectedCustomer?.confidence,
        confidenceAfter: 1.0,
      });
      apiService.logAction(correlationId, userId, userEmail, {
        type: 'select_shipto',
        previousValue: selectedShipTo?.number,
        newValue: shipTo.shipto_number,
        confidenceBefore: selectedShipTo?.confidence,
        confidenceAfter: 1.0,
      });
    }
    setSelectedCustomer({
      name: customer.customer_name,
      number: customer.customer_number,
      confidence: 1.0,
      manuallySelected: true,
    });
    setSelectedShipTo({
      name: shipTo.shipto_name,
      number: shipTo.shipto_number,
      address: shipTo.shipto_address,
      address2: shipTo.shipto_address2,
      city: shipTo.shipto_city,
      state: shipTo.shipto_state,
      zip: shipTo.shipto_zip,
      customerNumber: customer.customer_number,
      customerName: customer.customer_name,
      confidence: 1.0,
      manuallySelected: true,
    });
    setCustomerShipToSearchModal(null);
    setCustomerDropdownOpen(false);
    setShipToDropdownOpen(false);

    if (customer.customer_number !== selectedCustomer?.number) {
      // Customer changed — re-validate items, refetch defaults, re-check PO duplicate
      revalidateForCustomerChange(customer.customer_number, shipTo.shipto_number);
    } else {
      // Same customer, different ship-to only — just refetch order defaults
      apiService.fetchOrderDefaults(customer.customer_number, shipTo.shipto_number, correlationId)
        .then(result => {
          if (result) {
            setCarrierCodes(result.carrier_codes || []);
            setFobCodesFromApi(result.fob_codes || []);
            setWarehouseCodes(result.warehouse_codes || []);
            const defaults = result.defaults || {};
            if (defaults.fob_code) {
              setSelectedFobCode({ code: defaults.fob_code, source: defaults.fob_source });
            } else {
              setSelectedFobCode(null);
            }
            if (defaults.carrier_code) {
              setSelectedCarrierCode({ code: defaults.carrier_code, source: defaults.carrier_source });
            } else {
              setSelectedCarrierCode(null);
            }
            if (defaults.warehouse_code) {
              setSelectedWarehouse({ code: defaults.warehouse_code, source: defaults.warehouse_source });
            } else {
              setSelectedWarehouse(null);
            }
            if (defaults.ship_via) {
              setShipVia(defaults.ship_via);
            }
          }
        })
        .catch(err => console.warn('Order defaults re-fetch failed:', err));
    }
  }, [correlationId, user, selectedCustomer, selectedShipTo, revalidateForCustomerChange]);

  const updateItemNote = useCallback((index, noteText) => {
    setSelectedItems(prev => prev.map((item, i) =>
      i === index ? { ...item, note: noteText } : item
    ));
  }, []);

  const updateItemNotePrintCode = useCallback((index, printCode) => {
    setSelectedItems(prev => prev.map((item, i) =>
      i === index ? { ...item, notePrintCode: printCode } : item
    ));
  }, []);

  // Vendor shown on drop-ship lines (display/validate only — not sent to A+).
  // vendorResolved marks that we've attempted a lookup so LineItems doesn't
  // re-fetch on every render. `manual` flags a CSR-entered value so a late
  // auto-resolve response can't clobber it (vendorManuallySet is sticky).
  const updateItemVendor = useCallback((index, { vendorNumber, vendorName, manual = false }) => {
    setSelectedItems(prev => prev.map((item, i) =>
      i === index
        ? {
            ...item,
            vendorNumber: vendorNumber ?? null,
            vendorName: vendorName ?? null,
            vendorResolved: true,
            vendorManuallySet: manual || item.vendorManuallySet || false,
          }
        : item
    ));
  }, []);

  // Drop-ship OE Cost / GL Cost overrides (Tonya Conner, 2026-09-24). A+ copies these onto
  // the vendor PO only when the order is CREATED — fixing them afterward means fixing the
  // vendor PO by hand too — so the CSR sets them here, before submit. Stored as the input
  // string (≤5 decimals, as A+ shows them); `undefined` = not overridden, so getLineCosts shows
  // the A+ default (and an un-typed OE keeps following GL). Not yet sent to A+ (cXML pending Trey).
  // `commit` marks a finished edit (blur/Enter) so we log one action per edit, not per keystroke.
  const updateItemCost = useCallback((itemId, field, value, { commit = false } = {}) => {
    if (field !== 'oeCost' && field !== 'glCost') return;
    const item = selectedItems.find(it => it._id === itemId);
    if (commit && correlationId && item) {
      const previous = getLineCosts(item, true)[field];
      if (value !== previous) {
        apiService.logAction(correlationId, user?.userId || 'unknown', user?.userDetails || 'unknown', {
          type: field === 'oeCost' ? 'edit_oe_cost' : 'edit_gl_cost',
          lineIndex: item.piLineIndex ?? null,
          previousValue: previous || null,
          newValue: value || null,
        });
        trackEvent('csr_dropship_cost', { correlationId, field, hasValue: !!value });
      }
    }
    setSelectedItems(prev => prev.map(it =>
      it._id === itemId ? { ...it, [field]: value } : it
    ));
  }, [correlationId, user, selectedItems]);

  // Per-line warehouse override (Tonya Conner, 2026-09-04): a line defaults to the
  // order's header warehouse (warehouseId null here means "use the header warehouse");
  // setting this splits the order into multiple A+ submissions, one per distinct
  // warehouse, on the backend.
  const updateItemWarehouse = useCallback(async (index, warehouseCode) => {
    const line = selectedItems[index];
    if (!line) return;
    const lineId = line._id;
    const checkWarehouse = warehouseCode || selectedWarehouse?.code || null;

    // Drop the previous warehouse's stock immediately — leaving it up while the recheck is in
    // flight would show another warehouse's numbers against this one.
    setSelectedItems(prev => prev.map(item =>
      item._id === lineId
        ? { ...item, warehouseId: warehouseCode || null, inventory: null, inItembal: null,
            inventoryRechecking: !!(checkWarehouse && item.selectedItemNumber) }
        : item
    ));

    if (!checkWarehouse || !line.selectedItemNumber) return;

    let inv = null;
    let inItembal = null;
    let costs;
    try {
      const result = await apiService.recheckInventory(checkWarehouse, [line.selectedItemNumber], correlationId);
      inv = (result.inventory || {})[line.selectedItemNumber] || null;
      inItembal = deriveInItembal((result.in_itembal || {})[line.selectedItemNumber], inv);
      costs = result.costs;
    } catch (err) {
      // Leaves inItembal null → "Inventory unavailable — setup not verified" (never blocks
      // submit); _check_itembal still re-checks for real at submit.
      console.warn('Per-line warehouse inventory recheck failed (non-blocking):', err);
      trackEvent('inventory_recheck_failed', { correlationId, warehouse: checkWarehouse, error: err.message, perLine: true });
    }
    setSelectedItems(prev => prev.map(item => {
      if (item._id !== lineId) return item;
      // A faster second change can land first — only apply if this is still the line's warehouse.
      const current = item.warehouseId || selectedWarehouse?.code || null;
      if (current !== checkWarehouse) return item;
      return { ...item, inventory: inv, inItembal, inventoryRechecking: false, ...costFieldsFromRecheck(costs, line.selectedItemNumber) };
    }));
  }, [selectedItems, selectedWarehouse, correlationId]);

  const handleWarehouseChange = useCallback(async (warehouseCode) => {
    if (!warehouseCode) {
      setSelectedWarehouse(null);
      return;
    }

    const newWarehouse = { code: warehouseCode, source: 'manual' };
    setSelectedWarehouse(newWarehouse);

    // Skip recheck if same warehouse
    if (warehouseCode === selectedWarehouse?.code) return;

    // Recheck inventory if warehouse changed and there are matched items. Lines with their own
    // warehouse override don't ship from the header warehouse, so they keep their own stock.
    const itemNumbers = selectedItems
      .filter(item => item.selectedItemNumber && !item.warehouseId)
      .map(item => item.selectedItemNumber);

    if (itemNumbers.length === 0) return;

    setInventoryLoading(true);
    try {
      const result = await apiService.recheckInventory(warehouseCode, itemNumbers, correlationId);
      const inventoryMap = result.inventory || {};
      const itembalMap = result.in_itembal || {};

      setSelectedItems(prev => prev.map(item => {
        if (!item.selectedItemNumber || item.warehouseId) return item;
        const inv = inventoryMap[item.selectedItemNumber] || null;
        const inItembal = deriveInItembal(itembalMap[item.selectedItemNumber], inv);
        return { ...item, inventory: inv, inItembal, ...costFieldsFromRecheck(result.costs, item.selectedItemNumber) };
      }));
    } catch (err) {
      console.warn('Inventory recheck failed (non-blocking):', err);
      trackEvent('inventory_recheck_failed', { correlationId, warehouse: warehouseCode, error: err.message });
    } finally {
      setInventoryLoading(false);
    }
  }, [selectedItems, selectedWarehouse, correlationId]);

  // Add a new line item (from AddLineItemModal)
  const addLineItem = useCallback((data) => {
    const { lineNumber, item, quantity, stockUom, finalStockUomPrice, priceResult, customerUnit, customerDefaultUom } = data;

    // Compute piLineIndex beyond extraction range
    const maxPiIndex = selectedItems.reduce((max, it) => Math.max(max, it.piLineIndex), -1);
    const newPiLineIndex = maxPiIndex + 1;

    // Build the new selected item
    const newItem = {
      piLineIndex: newPiLineIndex,
      extQuantityWasNull: true,
      extQuantity: null,
      extUnitPrice: null,
      extTotal: null,
      _id: newLineId(),
      addedByCsr: true,
      poLineNumber: lineNumber,
      poItemNumber: null,
      poDescription: null,
      poQuantity: quantity,
      poUnitOfMeasure: stockUom,
      selectedItemNumber: item.item_number,
      selectedDescription: item.description,
      unitOfMeasure: stockUom,
      stockingUom2: item.unit_of_measure_2 || null,
      stockingUom3: item.unit_of_measure_3 || null,
      uomConversion1: item.uom_conversion_1 ?? null,
      uomConversion2: item.uom_conversion_2 ?? null,
      confidence: 1.0,
      confidenceLevel: 'high',
      issue: null,
      alternatives: [],
      valUnitPrice: priceResult?.val_unit_price ?? null,
      valPriceUom: priceResult?.val_price_unit_of_measure || null,
      valPriceUomConversion: priceResult?.val_price_unit_conversion || null,
      priceValid: true,
      priceVariance: null,
      priceVarianceLevel: null,
      priceIssue: null,
      priceStockUomResolved: false,
      priceStockUnitPrice: null,
      priceStockUomLabel: null,
      inventory: item.inventory || null,
      inItembal: deriveInItembal(item.in_itembal, item.inventory),
      aplusGlCost: item.aplus_gl_cost ?? null,
      costPricingUom: item.aplus_pricing_uom || null,
      costPricingPerStock: item.aplus_pricing_per_stock ?? null,
      isDropShip: false,
      note: '',
      // Preserve the CSR's entered price. On a ship-to/customer re-validation the added line's
      // resolution is nulled before the async price re-check runs, so without this the entered
      // price is lost and (when the new customer has no contract price) the line strands with no
      // price. _csrAddedPrice carries it forward as a fallback. Kept in sync in resolvePrice().
      _csrAddedPrice: finalStockUomPrice,
      resolution: {
        origin: 'csr_added',
        type: 'csr_added',
        quantity: quantity,
        uom: stockUom,
        unitPrice: finalStockUomPrice,
        // Keyed in the customer's default unit (2 SK -> 320 EA): A+ shows both.
        ...(customerUnit && { customerUom: customerUnit.uom, customerQuantity: customerUnit.quantity }),
      },
      customerDefaultUom: customerDefaultUom || null,
      wasManualSearch: true,
    };

    // Insert at the right position WITHOUT renumbering (see poLineSeq). Previously an insert
    // bumped poLineNumber on every line at or after the chosen number, so a customer's real
    // line 40 went to A+ — and onto their EDI invoice — as 41. A duplicate line number is the
    // CSR's explicit choice and is recoverable; a silently rewritten one is not.
    setSelectedItems(prev => {
      const ln = poLineSeq(lineNumber);
      let insertIdx = prev.length;
      for (let i = 0; i < prev.length; i++) {
        if (poLineSeq(prev[i].poLineNumber) > ln) {
          insertIdx = i;
          break;
        }
      }

      const result = [...prev];
      result.splice(insertIdx, 0, newItem);
      return result;
    });

    // Search results carry no A+ cost, so fetch the added line's set-up GL cost at its warehouse
    // for the GL/OE Cost + M% display. Costs only — the line's inventory came with the item.
    const costWarehouse = selectedWarehouse?.code;
    if (costWarehouse && item.item_number && item.aplus_gl_cost == null) {
      apiService.recheckInventory(costWarehouse, [item.item_number], correlationId)
        .then(r => {
          setSelectedItems(prev => prev.map(it =>
            it._id === newItem._id && !it.warehouseId ? { ...it, ...costFieldsFromRecheck(r.costs, item.item_number) } : it
          ));
        })
        .catch(err => console.warn('Cost lookup for added line failed (non-blocking):', err));
    }

    // Log PI action
    if (correlationId) {
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';
      apiService.logAction(correlationId, userId, userEmail, {
        type: 'add_line_item',
        lineIndex: newPiLineIndex,
        newValue: item.item_number,
      });
    }

    trackEvent('csr_add_line_item', { correlationId, itemNumber: item.item_number });
  }, [selectedItems, selectedWarehouse, correlationId, user]);

  const handlePONumberChange = useCallback((newPoNumber) => {
    const originalPoNumber = extractedData?.po_number;
    setEditedPoNumber(newPoNumber);

    // Log to PI
    if (correlationId) {
      const userId = user?.userId || 'unknown';
      const userEmail = user?.userDetails || 'unknown';
      apiService.logAction(correlationId, userId, userEmail, {
        type: 'po_number_changed',
        originalPoNumber,
        newPoNumber,
      });
    }

    // Re-check duplicate
    const customerNumber = selectedCustomer?.number;
    checkPODuplicate(newPoNumber, customerNumber);
  }, [extractedData, correlationId, user, selectedCustomer, checkPODuplicate]);

  // Submit to ERP. { overrideTotalMismatch: true } resubmits after the CSR chose "Submit anyway"
  // on a PO total vs A+ total mismatch (the button's click event never carries it).
  const handleSubmitToERP = useCallback(async (options) => {
    if (selectedItems.length === 0) return;
    const overrideTotalMismatch = options?.overrideTotalMismatch === true;
    setSubmitError(null);
    setTotalMismatch(null);
    setStage('submitting');
    
    // Get user info for audit
    const userId = user?.userId || 'unknown';
    const userEmail = user?.userDetails || 'unknown';
    
    try {
      const orderData = {
        poNumber: editedPoNumber ?? extractedData.po_number,
        poDate: extractedData.po_date,
        requestedShipDate: requestedShipDate || null,
        customerNumber: selectedCustomer?.number,
        customerName: selectedCustomer?.name,
        shipToNumber: selectedShipTo?.number,
        fobCode: selectedFobCode?.code || null,
        carrierCode: selectedCarrierCode?.code || null,
        warehouseId: selectedWarehouse?.code || null,
        shipVia: shipVia || null,
        contactName: extractedData.contact_name || '',
        contactPhone: extractedData.contact_phone || '',
        shipToAddress: {
          name: selectedShipTo?.name,
          address: selectedShipTo?.address,
          city: selectedShipTo?.city,
          state: selectedShipTo?.state,
          zip: selectedShipTo?.zip
        },
        items: selectedItems.map((item, index) => {
          const res = item.resolution;
          const line = aplusOrderLine(item, normalizeUom);
          return {
            lineNumber: item.poLineNumber || (index + 1),
            itemNumber: item.selectedItemNumber,
            description: item.selectedDescription,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            unitOfMeasure: line.uom,
            ...(line.customer && { customerUnitOfMeasure: line.customer.uom, customerQuantity: line.customer.quantity }),
            ...(res && { resolution: { origin: res.origin, type: res.type } }),
            isDropShip: item.isDropShip || false,
            vendorNumber: item.vendorNumber || null,
            note: item.note || '',
            notePrintCode: item.notePrintCode || 'both',
            // The customer's own item number, for the order confirmation.
            customerItemNumber: item.poItemNumber || null,
            // Per-line warehouse override — null means "use the header warehouse".
            // Backend splits the order across warehouses when lines disagree.
            warehouseId: item.warehouseId || null,
          };
        }),
        subtotal: extractedData.subtotal,
        tax: extractedData.tax,
        total: extractedData.total,
        notes: extractedData.notes,
        orderNotes: orderNotes,
        orderComments: orderComments
          .filter(c => (c.text || '').trim())
          .map(c => ({ text: c.text.trim(), printCode: c.printCode })),
        headerDropShip: headerDropShip,
        aiOrderNumber: aiOrderNumber,
        ...(overrideTotalMismatch && { overrideTotalMismatch: true }),
      };

      // Build processing intelligence metadata
      const processingIntelligence = {
        customerManuallySelected: selectedCustomer?.manuallySelected || false,
        shipToManuallySelected: selectedShipTo?.manuallySelected || false,
        reviewStartedAt: reviewStartedAtRef.current,
        submittedAt: Date.now(),
        lineDetails: selectedItems.map((item, index) => ({
          piLineIndex: item.piLineIndex,
          wasManualSearch: item.wasManualSearch || false,
          wasAddedByCsr: item.addedByCsr || false,
          quantityManualEntry: item.extQuantityWasNull && item.poQuantity != null,
          quantityEdited: !item.extQuantityWasNull && item.poQuantity !== item.extQuantity,
          resolutionType: item.resolution?.type || null,
          resolutionOrigin: item.resolution?.origin || null,
        })),
      };

      const result = await apiService.submitToERP(orderData, correlationId, userId, userEmail, processingIntelligence);

      // Multi-warehouse split (backend, 2026-09-04): result.splits is present whenever
      // lines shipped from more than one warehouse. Any split that succeeded is now a
      // real A+ order that must never be resubmitted, so ANY split result — full,
      // partial, or (here, since a total split failure throws below) impossible to
      // reach with zero successes — takes the CSR to the Submitted screen rather than
      // back to Review, unlike a single-order failure which is always safe to retry.
      if (result.splits) {
        trackEvent('po_submitted_multi_warehouse', {
          correlationId, aiOrderNumber, partial: !!result.partial,
          splitCount: result.splits.length,
          successCount: result.splits.filter(s => s.success).length,
          totalReviewDurationMs: Date.now() - reviewStartedAtRef.current,
          lineCount: selectedItems.length,
        });
        setSubmissionSplits(result.splits);
        setStage('submitted');
        return;
      }

      if (result.success) {
        trackEvent('po_submitted', { correlationId, aiOrderNumber, totalReviewDurationMs: Date.now() - reviewStartedAtRef.current, lineCount: selectedItems.length, alreadySubmitted: !!result.alreadySubmitted });
        setErpOrderNumber(result.orderNumber);
        setAlreadySubmitted(!!result.alreadySubmitted);
        setStage('submitted');
      } else {
        throw new Error(result.error || 'A+ submission failed');
      }
    } catch (err) {
      console.error('ERP submission error:', err);
      // PO total and A+ total differ: nothing was sent. Back to Review with the figures so
      // the CSR can fix the lines or submit anyway (Tonya, 2026-10-22).
      if (err.totalMismatch) {
        trackEvent('po_submit_total_mismatch', { correlationId, aiOrderNumber, ...err.totalMismatch });
        setTotalMismatch(err.totalMismatch);
        setStage('review');
        return;
      }
      trackEvent('po_submission_error', { correlationId, errorMessage: err.message });
      // A total multi-warehouse failure (every split failed) still throws — nothing
      // succeeded, so it's safe to keep the CSR on Review to fix and retry, same as a
      // single-order failure. Surface the per-split reasons if the backend sent any.
      if (err.splits) {
        const detail = err.splits
          .filter(s => !s.success)
          .map(s => `warehouse ${s.warehouseId}: ${s.error}`)
          .join('; ');
        setSubmitError(detail || err.message || 'Failed to submit order to A+. Please review and try again.');
      } else {
        // Keep the CSR on the Review screen with their work intact so they can fix and
        // resubmit, rather than dropping them on a dead-end error screen.
        setSubmitError(err.message || 'Failed to submit order to A+. Please review and try again.');
      }
      setStage('review');
    }
  }, [extractedData, editedPoNumber, requestedShipDate, selectedCustomer, selectedShipTo, selectedItems, selectedFobCode, selectedCarrierCode, selectedWarehouse, shipVia, orderNotes, orderComments, headerDropShip, correlationId, user, aiOrderNumber]);

  // Reset application
  const handleReset = useCallback(() => {
    // Revoke blob URL to free memory (only for local blob URLs, not Office viewer URLs)
    if (filePreviewUrl && previewType === 'pdf') {
      URL.revokeObjectURL(filePreviewUrl);
    }
    // Land back on the dashboard, not a blank upload screen — "Upload a new PO" there
    // is one click away, and this is where a CSR should see what's still in flight.
    setStage('dashboard');
    setSubmitError(null);
    setTotalMismatch(null);
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setPreviewType(null);
    setTextContent(null);
    setCorrelationId(null);
    setExtractedData(null);
    setValidationResult(null);
    setSelectedCustomer(null);
    setSelectedShipTo(null);
    setSelectedItems([]);
    setHeaderDropShip(false);
    setRequestedShipDate(null);
    setCarrierCodes([]);
    setFobCodesFromApi([]);
    setWarehouseCodes([]);
    setSelectedFobCode(null);
    setSelectedCarrierCode(null);
    setSelectedWarehouse(null);
    setInventoryLoading(false);
    setShipVia('');
    setOrderNotes('');
    setOrderComments([{ text: '', printCode: 'both' }]);
    setEditedPoNumber(null);
    setPoNumberWarning(null);
    setError(null);
    setErpOrderNumber(null);
    setAlreadySubmitted(false);
    setSubmissionSplits(null);
    setAiOrderNumber(null);
    setExtractionRetryData(null);
    setItemsRevalidating(false);
    setItemRevalidationFailed(false);
    setZoom(100);
    reviewStartedAtRef.current = null;
    revalidationRef.current = 0;
    setCustomerShipToSearchModal(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [filePreviewUrl, previewType]);

  const handleRetryExtraction = useCallback(async () => {
    if (!extractionRetryData) return;
    const { base64Content, fileName, mimeType, correlationId: cid, userId, userEmail } = extractionRetryData;

    trackEvent('po_extraction_retry', { correlationId: cid });
    setStage('processing');
    setIsResuming(false);
    setProcessingStatus('Extracting order data...');
    setError(null);
    setExtractionRetryData(null); // Clear before attempting — re-set only on timeout

    try {
      const extractStartTime = Date.now();
      const extractResult = await apiService.extractPOWithPolling(
        base64Content, fileName, mimeType, cid, userId, userEmail,
        () => setProcessingStatus('Extracting order data...')
      );
      trackEvent('po_extraction_complete', { correlationId: cid, durationMs: Date.now() - extractStartTime, itemCount: extractResult.po_data?.items?.length || 0 });

      const poData = extractResult.po_data;
      await runPostExtractionPipeline(poData, cid, userId, userEmail, extractStartTime);
    } catch (err) {
      console.error('Extraction retry error:', err);
      trackEvent('po_processing_error', { correlationId: cid, stage: 'extract_retry', errorMessage: err.message });
      if (err.message?.includes('timed out')) {
        setExtractionRetryData({ base64Content, fileName, mimeType, correlationId: cid, userId, userEmail });
      }
      setError(err.message || 'Failed to process PO. Please try again.');
      setStage('error');
    }
  }, [extractionRetryData]);

  // DEV-ONLY: open the app with ?mock=1 to load a sample order straight into the
  // Review screen so the UI can be previewed without a working backend. Crafted to
  // surface every recent change at once (Line Comment, FOB-required, "Not Set up in
  // ITEMBAL"). import.meta.env.DEV strips this entire block from production builds.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!new URLSearchParams(window.location.search).has('mock')) return;
    setExtractedData({
      po_number: '1008444913',
      po_date: '01/09/2026',
      requested_delivery_date: '2026-07-06',
      contact_name: 'Critt Gore',
      contact_phone: '843 267 2440',
      contact_email: '',
      subtotal: 14434.50,
      tax: 0,
      total: 14434.50,
      notes: 'DAP delivery. Quote PO number on all correspondence.',
    });
    setAiOrderNumber('AI-100123');
    // Left null to demonstrate the requested-ship-date-required gate: the mock PO's date
    // (2026-07-06) is in the past, which the post-extraction logic now ignores/forces.
    setRequestedShipDate(null);
    setSelectedCustomer({ name: 'MARS PETCARE US INC', number: '130087', confidence: 0.95 });
    setSelectedShipTo({
      name: 'COLUMBIA PLANT - PETCARE', number: '1',
      address: '1720 Pineview Drive', address2: '',
      city: 'Columbia', state: 'SC', zip: '29209', confidence: 0.95,
    });
    setValidationResult({
      shipto_validations: {
        shipto_confidence: 'high', shipto_confidence_icon: '✓', shipto_confidence_score: 0.95,
        val_shipto_customer_number: '130087', shipto_alternatives: [],
      },
      customer_validations: { po_customer_name: 'Mars Petcare US, Inc.' },
      item_validations: [], overall_confidence: 'high', validation_issues: [],
    });
    setCarrierCodes([{ code: 'UPS', description: 'UPS Ground' }]);
    setFobCodesFromApi([{ code: 'FFA', description: 'Freight/Freight Allowance' }]);
    setWarehouseCodes([{ code: '01', description: 'Wilmington' }]);
    setOrderComments([
      { text: 'Palletize by SKU, do not mix pallets', printCode: 'both' },
      { text: 'Hold for AP credit review before ship', printCode: 'internal' },
    ]);
    setSelectedWarehouse({ code: '01', source: 'mock' });
    // selectedFobCode intentionally left null to demonstrate the FOB-required gate (#7).
    setSelectedItems([
      {
        _id: newLineId(), piLineIndex: 0, extQuantityWasNull: false, poLineNumber: '00010',
        poItemNumber: '40821', poDescription: 'Sfilm 12in Width US',
        poQuantity: 2685, poUnitOfMeasure: 'KG',
        selectedItemNumber: '40821', selectedDescription: 'SFILM 12IN WIDTH US', unitOfMeasure: 'KG',
        confidence: 0.98, confidenceLevel: 'high', issue: null, alternatives: [],
        valUnitPrice: 3.70, valPriceUom: 'KG', valPriceUomConversion: 1,
        priceValid: true, priceVariance: 0, priceVarianceLevel: 'exact', priceIssue: null,
        priceStockUomResolved: false, priceStockUnitPrice: null, priceStockUomLabel: null,
        inventory: { quantity_on_hand: 5000, warehouse_id: '01' },
        isDropShip: false, note: '',
        extQuantity: 2685, extUnitPrice: 3.70, extTotal: 9934.50, addedByCsr: false,
      },
      {
        _id: newLineId(), piLineIndex: 1, extQuantityWasNull: false, poLineNumber: '00020',
        poItemNumber: '50999', poDescription: 'Poly Liner 18in',
        poQuantity: 100, poUnitOfMeasure: 'CS',
        selectedItemNumber: '50999', selectedDescription: 'POLY LINER 18IN', unitOfMeasure: 'CS',
        confidence: 0.92, confidenceLevel: 'high', issue: null, alternatives: [],
        valUnitPrice: 45.00, valPriceUom: 'CS', valPriceUomConversion: 1,
        priceValid: true, priceVariance: 0, priceVarianceLevel: 'exact', priceIssue: null,
        priceStockUomResolved: false, priceStockUnitPrice: null, priceStockUomLabel: null,
        inventory: null, // null => "Not Set up in ITEMBAL" badge (#8a) + hard-block on submit (#8b)
        isDropShip: false, note: '',
        extQuantity: 100, extUnitPrice: 45.00, extTotal: 4500.00, addedByCsr: false,
      },
      {
        // Low-confidence item to show the Order Pad "Previously Ordered" badge (#3)
        _id: newLineId(), piLineIndex: 2, extQuantityWasNull: false, poLineNumber: '00030',
        poItemNumber: 'CB18X12', poDescription: 'Corrugated Box 18x12x10',
        poQuantity: 500, poUnitOfMeasure: 'CS',
        selectedItemNumber: '31204', selectedDescription: 'CORR BOX 18X12X10 RSC', unitOfMeasure: 'CS',
        confidence: 0.65, confidenceLevel: 'medium', issue: 'Item number mismatch - matched by description',
        alternatives: [
          {
            item_number: '31205', description: 'CORR BOX 18X12X10 RSC KRAFT',
            unit_of_measure: 'CS', price_unit_of_measure: 'CS', price_unit_conversion: 1,
            confidence: 0.85, match_source: 'order_pad', inventory: null,
          },
          {
            item_number: '31206', description: 'CORR BOX 18X12X10 200# WHITE',
            unit_of_measure: 'CS', price_unit_of_measure: 'CS', price_unit_conversion: 1,
            confidence: 0.72, match_source: 'azure_search', inventory: null,
          },
        ],
        valUnitPrice: 2.15, valPriceUom: 'CS', valPriceUomConversion: 1,
        priceValid: true, priceVariance: 0.02, priceVarianceLevel: 'warning', priceIssue: null,
        priceStockUomResolved: false, priceStockUnitPrice: null, priceStockUomLabel: null,
        inventory: { quantity_on_hand: 1200, warehouse_id: '01' },
        isDropShip: false, note: '',
        extQuantity: 500, extUnitPrice: 2.10, extTotal: 1050.00, addedByCsr: false,
      },
    ]);
    setStage('review');
  }, []);

  // Show loading spinner while checking auth (must be after all hooks)
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  }

  // Build list of unresolved issues for the attention panel
  const unresolvedIssues = [];

  // Customer — low confidence and not manually selected
  if ((selectedCustomer?.confidence ?? 1) < 0.8 && !selectedCustomer?.manuallySelected) {
    unresolvedIssues.push({
      text: `Customer · Low confidence match (${Math.round((selectedCustomer?.confidence ?? 0) * 100)}%) — select correct customer`,
      itemIndex: null,
      type: 'customer',
    });
  }

  // Ship-to — low confidence and not manually selected
  if ((selectedShipTo?.confidence ?? 1) < 0.8 && !selectedShipTo?.manuallySelected) {
    unresolvedIssues.push({
      text: `Ship-To · Low confidence match (${Math.round((selectedShipTo?.confidence ?? 0) * 100)}%) — select correct address`,
      itemIndex: null,
      type: 'shipto',
    });
  }

  // FOB code — always mandatory (the valid FOB codes are always available to choose from)
  if (!selectedFobCode?.code) {
    unresolvedIssues.push({
      text: `FOB Code · Required — select a FOB code in the order header`,
      itemIndex: null,
      type: 'fob_required',
    });
  }

  // Line item issues — one per item, first matching condition wins
  selectedItems.forEach((item, index) => {
    const lineNum = item.poLineNumber || (index + 1);
    const uomMismatch = item.priceIssue?.includes('UOM Mismatch') || false;
    // Flag ANY non-zero price difference from the A+ contract, not just >2% — some customers
    // reject invoices that aren't penny-exact, so the CSR must resolve every difference before
    // submit (Tonya, 2026-07-22). 'warning' (<=2%) and 'invalid' (>2%) both require resolution.
    const priceVariance = !uomMismatch
      && (item.priceVarianceLevel === 'warning' || item.priceVarianceLevel === 'invalid')
      && item.valUnitPrice != null && item.priceVariance != null;

    // b1: matched by DESCRIPTION (best guess), not by item number / cross-reference — the case
    // where a wrong item slips in (plastic→tape, turkey→packaging). Require the CSR to confirm it
    // before submit. Changing the item via search clears item.issue, so this only gates the
    // original auto-guess; number / cross-ref / order-pad matches never set this issue.
    const matchedByDescription = !!item.issue && item.issue.includes('matched by description');

    if (!item.selectedItemNumber) {
      unresolvedIssues.push({
        text: `Line ${lineNum} · No matching A+ item — search catalog`,
        itemIndex: index,
        type: 'unmatched',
      });
    } else if (matchedByDescription && !item.itemConfirmed) {
      unresolvedIssues.push({
        text: `Line ${lineNum} · Matched by description — confirm this is the correct A+ item`,
        itemIndex: index,
        type: 'confirm_item',
      });
    } else if (deriveInItembal(item.inItembal, item.inventory) === false) {
      // Hard error: item has NO ITBAL row for the selected warehouse (in_itembal false), so A+
      // would silently drop it. Block until the CSR fixes it (all-or-none). This is distinct
      // from being merely out of stock (ITBAL row exists, 0 on hand) — that's allowed to
      // backorder and does not block.
      // Applies to drop-ship lines too. Per Rhea (A+ business owner, 2026-08-21): "The check
      // should be to make sure the ORDER.ODITNO/ODWHID has a match in ITBAL.IBITNO/IBWHID. The
      // inventory quantity does not matter... if a user keys an order (drop ship or not) in a
      // warehouse and tries to add an item not set up there, it will error out." A+ requires the
      // ITBAL row to exist regardless of drop-ship status — only the on-hand QUANTITY is
      // irrelevant for drop-ship, and this rule already ignores quantity. The old `!item.isDropShip
      // && !headerDropShip` exemption here let order AI1GU (PO 4582225960, item 6212475, WH 26)
      // submit successfully from the CSR's side while A+ rejected it days later with "713: This
      // item is not stocked in this warehouse" — a false-success screen for an order that never
      // reached A+.
      // Compare `=== false` so only a DEFINITIVE "not set up" blocks: null means the ITBAL
      // lookup couldn't answer, and blocking on that turns a transient SQL failure into an
      // unprocessable order. `_check_itembal` still rejects genuinely un-set-up items at submit.
      unresolvedIssues.push({
        text: `Line ${lineNum} · Not set up in ITEMBAL — change warehouse, remove the item, or have it set up in A+`,
        itemIndex: index,
        type: 'not_in_itembal',
      });
    } else if (item.confidenceLevel === 'low' && !item.resolution && !item.itemConfirmed) {
      // `!item.itemConfirmed` matters: a description match below the 0.60 medium threshold trips
      // BOTH this rule and the confirm_item rule above. Clicking "Confirm item" cleared only that
      // one, so the banner flipped to "✓ Item confirmed" while submit stayed blocked by a message
      // that also said "confirm correct item" — reading exactly like the confirm hadn't worked.
      // Tonya hit this in prod on a 58% match (item 6184427, PO item 000003441) and found the
      // workaround herself: re-selecting the same item via search rebuilds the line at
      // confidence 1.0 / 'high', which clears the rule. One explicit confirmation should satisfy
      // both — it is the same question being asked twice.
      // Not a loophole: the Confirm button only renders for description matches (LineItems.jsx),
      // so a low-confidence line without one still has to be changed or resolved.
      unresolvedIssues.push({
        text: `Line ${lineNum} · Low confidence match (${Math.round(item.confidence * 100)}%) — pick the right item from Alternatives or Search A+ Catalog`,
        itemIndex: index,
        type: 'low_confidence',
      });
    } else if (item.selectedItemNumber && item.poQuantity == null && !item.resolution) {
      unresolvedIssues.push({
        text: `Line ${lineNum} · Quantity not found on PO — enter quantity`,
        itemIndex: index,
        type: 'missing_qty',
      });
    } else if (uomMismatch && !item.resolution) {
      // Contract lines read "PO=EA, Expected=M"; lines without one read "PO=EA, Stock=M".
      const uomMatch = item.priceIssue?.match(/PO=(\w+),?\s*(?:Stock|Expected)=(\w+)/);
      const poUom = uomMatch?.[1] || '?';
      const stockUom = uomMatch?.[2] || '?';
      unresolvedIssues.push({
        text: `Line ${lineNum} · UOM mismatch (PO: ${poUom}, Stock: ${stockUom}) — resolve`,
        itemIndex: index,
        type: 'uom_mismatch',
      });
    } else if (priceVariance && (!item.resolution || item.resolution.type === 'uom_auto_1to1')) {
      // A 1:1 UOM auto-match sets item.resolution but never confirmed the PRICE, so still
      // require a price resolution when it differs from the A+ contract (e.g. line 8: EA->RL
      // auto-matched, yet PO $1.39 vs contract $1.38). A CSR price resolution replaces it.
      const pct = Math.abs(item.priceVariance * 100).toFixed(1);
      unresolvedIssues.push({
        text: `Line ${lineNum} · PO price differs from A+ (${pct}%) — resolve`,
        itemIndex: index,
        type: 'price_variance',
      });
    } else if (item.addedByCsr && !item.resolution) {
      unresolvedIssues.push({
        text: `Line ${lineNum} · New line item — pricing not yet resolved`,
        itemIndex: index,
        type: 'csr_added',
      });
    } else if (item.selectedItemNumber && !item.resolution) {
      // Check if effective UOM is a valid stocking UOM
      const effectiveUom = item.poUnitOfMeasure;
      if (effectiveUom && !isValidStockingUom(effectiveUom, item)) {
        const validUoms = [item.unitOfMeasure, item.stockingUom2, item.stockingUom3]
          .filter(u => u && u.trim());
        unresolvedIssues.push({
          text: `Line ${lineNum} · UOM '${effectiveUom}' is not a valid stocking unit — resolve to ${validUoms.join(' or ')}`,
          itemIndex: index,
          type: 'invalid_stocking_uom',
        });
      }
    }

    // Additional check: if item has a resolution, verify its UOM is a valid stocking UOM
    if (item.selectedItemNumber && item.resolution) {
      if (!isValidStockingUom(item.resolution.uom, item)) {
        const validUoms = [item.unitOfMeasure, item.stockingUom2, item.stockingUom3]
          .filter(u => u && u.trim());
        unresolvedIssues.push({
          text: `Line ${lineNum} · Resolved UOM '${item.resolution.uom}' is not a valid stocking unit — re-resolve to ${validUoms.join(' or ')}`,
          itemIndex: index,
          type: 'invalid_stocking_uom',
        });
      }
    }
  });

  const unresolvedCount = unresolvedIssues.length;

  // Lines whose open issue is only a PO-vs-A+ price difference can be resolved together.
  // Outliers (far bigger gap than a price-list difference) are offered separately.
  const bulkPriceLines = unresolvedIssues
    .filter(issue => issue.type === 'price_variance' && selectedItems[issue.itemIndex])
    .map(issue => ({ item: selectedItems[issue.itemIndex], line: selectedItems[issue.itemIndex].poLineNumber || issue.itemIndex + 1 }));
  const bulkRegular = bulkPriceLines.filter(l => !isBulkOutlier(l.item)).map(l => l.item);
  const bulkAll = bulkPriceLines.map(l => l.item);
  const bulkPrice = bulkAll.length >= 2 ? {
    count: bulkAll.length,
    regularCount: bulkRegular.length,
    outliers: bulkPriceLines
      .filter(l => isBulkOutlier(l.item))
      .map(l => ({ line: l.line, pct: Math.abs(l.item.priceVariance * 100).toFixed(1) })),
    totalsAll: bulkPriceTotals(bulkAll),
    totalsRegular: bulkPriceTotals(bulkRegular),
    onApply: (choice, includeOutliers) =>
      resolvePricesBulk(choice, (includeOutliers ? bulkAll : bulkRegular).map(item => item._id)),
  } : null;

  const canAddLineItem = ((selectedCustomer?.confidence ?? 0) >= 0.8 || !!selectedCustomer?.manuallySelected)
    && ((selectedShipTo?.confidence ?? 0) >= 0.8 || !!selectedShipTo?.manuallySelected);

  // Dashboard Screen — default landing surface for an authenticated CSR
  if (stage === 'dashboard') {
    return (
      <MyPOsDashboard
        user={user}
        onUploadNew={() => setStage('upload')}
        onResume={handleResumeFromDashboard}
      />
    );
  }

  // Upload Screen
  if (stage === 'upload') {
    return (
      <UploadStage
        user={user}
        error={error}
        isDragging={isDragging}
        fileInputRef={fileInputRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onInputChange={handleInputChange}
        onClearError={() => setError(null)}
        onBackToDashboard={() => setStage('dashboard')}
        multiple={asyncEnabled}
      />
    );
  }

  // Processing Screen
  if (stage === 'processing') {
    return (
      <ProcessingStage
        processingStatus={processingStatus}
        fileName={selectedFile?.name}
        heading={isBatchUploading ? 'Uploading Orders' : isResuming ? 'Refreshing Order' : 'Processing Order'}
      />
    );
  }

  // Submitting Screen
  if (stage === 'submitting') {
    return <SubmittingStage />;
  }

  // Submitted Screen
  if (stage === 'submitted') {
    return (
      <SubmittedStage
        aiOrderNumber={aiOrderNumber}
        poNumber={editedPoNumber ?? extractedData?.po_number}
        splits={submissionSplits}
        alreadySubmitted={alreadySubmitted}
        correlationId={correlationId}
        onReset={handleReset}
      />
    );
  }

  // Error Screen
  if (stage === 'error') {
    return <ErrorStage error={error} onReset={handleReset} onRetry={extractionRetryData ? handleRetryExtraction : undefined} />;
  }

  // Review Screen
  return (
    <>
    <TotalMismatchModal
      mismatch={totalMismatch}
      onClose={() => setTotalMismatch(null)}
      onSubmitAnyway={() => {
        trackEvent('po_submit_total_mismatch_overridden', { correlationId, aiOrderNumber, ...totalMismatch });
        apiService.logAction(correlationId, user?.userId || 'unknown', user?.userDetails || 'unknown', {
          type: 'total_mismatch_overridden', ...totalMismatch,
        });
        handleSubmitToERP({ overrideTotalMismatch: true });
      }}
    />
    <ReviewStage
      aiOrderNumber={aiOrderNumber}
      editedPoNumber={editedPoNumber}
      poNumberWarning={poNumberWarning}
      onPONumberChange={handlePONumberChange}
      user={user}
      extractedData={extractedData}
      validationResult={validationResult}
      selectedFile={selectedFile}
      filePreviewUrl={filePreviewUrl}
      previewType={previewType}
      textContent={textContent}
      zoom={zoom}
      selectedCustomer={selectedCustomer}
      selectedShipTo={selectedShipTo}
      selectedItems={selectedItems}
      itemsRevalidating={itemsRevalidating}
      itemRevalidationFailed={itemRevalidationFailed}
      requestedShipDate={requestedShipDate}
      onRequestedShipDateChange={setRequestedShipDate}
      fobCodes={fobOptionsWithDefault(selectedFobCode, fobCodesFromApi)}
      carrierCodes={carrierCodes}
      selectedFobCode={selectedFobCode}
      selectedCarrierCode={selectedCarrierCode}
      onFobCodeChange={setSelectedFobCode}
      onCarrierCodeChange={setSelectedCarrierCode}
      warehouseCodes={warehouseCodes}
      selectedWarehouse={selectedWarehouse}
      onWarehouseChange={handleWarehouseChange}
      inventoryLoading={inventoryLoading}
      shipVia={shipVia}
      onShipViaChange={setShipVia}
      orderNotes={orderNotes}
      onOrderNotesChange={setOrderNotes}
      orderComments={orderComments}
      onOrderCommentsChange={setOrderComments}
      headerDropShip={headerDropShip}
      onHeaderDropShipChange={setHeaderDropShip}
      customerDropdownOpen={customerDropdownOpen}
      shipToDropdownOpen={shipToDropdownOpen}
      unresolvedCount={unresolvedCount}
      unresolvedIssues={unresolvedIssues}
      bulkPrice={bulkPrice}
      onZoomIn={() => setZoom(Math.min(200, zoom + 25))}
      onZoomOut={() => setZoom(Math.max(50, zoom - 25))}
      onCustomerDropdownToggle={() => setCustomerDropdownOpen(!customerDropdownOpen)}
      onSelectCustomer={(customer) => {
        if (correlationId) {
          const uid = user?.userId || 'unknown';
          const uemail = user?.userDetails || 'unknown';
          apiService.logAction(correlationId, uid, uemail, {
            type: customer.manuallySelected ? 'accept_customer' : 'select_customer',
            previousValue: selectedCustomer?.number,
            newValue: customer.number,
            confidenceBefore: selectedCustomer?.confidence,
            confidenceAfter: customer.confidence,
          });
        }
        setSelectedCustomer(customer);
        setCustomerDropdownOpen(false);
        if (customer.number !== selectedCustomer?.number) {
          // Customer changed — re-validate items, refetch defaults, re-check PO duplicate
          revalidateForCustomerChange(customer.number, selectedShipTo?.number);
        } else {
          // Same customer (e.g., accept current match) — just re-check PO duplicate
          const poNum = editedPoNumber ?? extractedData?.po_number;
          checkPODuplicate(poNum, customer.number);
        }
      }}
      onShipToDropdownToggle={() => setShipToDropdownOpen(!shipToDropdownOpen)}
      salesRep={salesRep}
      onSelectShipTo={(shipTo) => {
        if (correlationId) {
          const uid = user?.userId || 'unknown';
          const uemail = user?.userDetails || 'unknown';
          apiService.logAction(correlationId, uid, uemail, {
            type: shipTo.manuallySelected ? 'accept_shipto' : 'select_shipto',
            previousValue: selectedShipTo?.number,
            newValue: shipTo.number,
            confidenceBefore: selectedShipTo?.confidence,
            confidenceAfter: shipTo.confidence,
          });
        }
        setSelectedShipTo(shipTo);
        setSelectedCustomer({
          name: shipTo.customerName,
          number: shipTo.customerNumber,
          confidence: shipTo.confidence,
          manuallySelected: true,
        });
        setCustomerDropdownOpen(false);
        setShipToDropdownOpen(false);
        if (shipTo.customerNumber !== selectedCustomer?.number) {
          // Customer changed via ship-to selection — re-validate items, refetch defaults, re-check PO duplicate
          revalidateForCustomerChange(shipTo.customerNumber, shipTo.number);
        } else {
          // Same customer — just re-check PO duplicate
          const poNum = editedPoNumber ?? extractedData?.po_number;
          checkPODuplicate(poNum, shipTo.customerNumber);
        }
      }}
      correlationId={correlationId}
      onUpdateItemSelection={updateItemSelection}
      onConfirmItem={updateItemConfirmed}
      onUpdateItemQuantity={updateItemQuantity}
      onValidatePrice={validatePriceForItem}
      onResolvePrice={resolvePrice}
      onDeleteLineItem={deleteLineItem}
      onUpdateNote={updateItemNote}
      onUpdateNotePrintCode={updateItemNotePrintCode}
      onUpdateItemVendor={updateItemVendor}
      onUpdateItemCost={updateItemCost}
      onUpdateItemWarehouse={updateItemWarehouse}
      onAddLineItem={addLineItem}
      canAddLineItem={canAddLineItem}
      onSubmitToERP={handleSubmitToERP}
      onReset={handleReset}
      submitError={submitError}
      onClearSubmitError={() => setSubmitError(null)}
      customerShipToSearchModal={customerShipToSearchModal}
      onOpenCustomerShipToSearch={(initialStep) => setCustomerShipToSearchModal({ initialStep })}
      onCustomerShipToSearchSelect={handleCustomerShipToSearchSelect}
      onCloseCustomerShipToSearch={() => setCustomerShipToSearchModal(null)}
    />
    </>
  );
}
